import React, { useEffect, useRef, useState } from 'react';
import { 
  UserProfile, PromoCode, PromoRedemption, AdminAction,
  EventLog, UserWithStatus, UserTimeline, IPAnalytics, DomainAnalytics,
  UserRiskStatus, ControlStatus, DomainControlStatus,
  Generation, CreditLedgerEntry, GenerationStatus, SalesMetrics, SalesPeriod,
  SalesTransaction, UserPaymentRow, UserCostSummary, UserResource
} from '../types';
import { getOperationDefinition, APP_DEFAULT_OPERATION } from '@rehub/ai-transparency';
import IPWorldMap from './IPWorldMap';
import UserLocationMap from './UserLocationMap';
import ShareDemoManager from './ShareDemoManager';
import AdminSignupsPanel from './AdminSignupsPanel';
import AdminAITransparencyPanel from './AdminAITransparencyPanel';
import SubscriptionsTab from './admin/SubscriptionsTab';
import {
  Shield, Search, Zap, Users, Gift, Activity, X, Check, 
  AlertCircle, Loader2, Plus, Edit3, XCircle, CheckCircle,
  TrendingUp, DollarSign, Award, Calendar, Clock,
  Globe, Wifi, Eye, Ban, AlertTriangle, ChevronRight, ChevronLeft,
  RefreshCw, Filter, User, ArrowLeft, Image, CreditCard,
  ExternalLink, Coins, Trash2, BarChart3, Percent, Target,
  Download,
  ClipboardList,
  Play,
  Upload,
  Send,
  MapPin,
  Film,
  Globe as GlobeIcon,
  Share2,
  Mail,
  UserPlus,
  Sparkles,
  FileText,
  Monitor,
  Copy,
  Link2,
} from 'lucide-react';
import { openHubAdminUser } from '@rehub/admin';
import { formatChargedAmount, formatProviderCostUsd, formatRecordedRevenue, recordedRevenueCaption } from '@rehub/pricing';
import { AdminShell } from '@rehub/admin/ui';
import {
  searchUsers,
  grantBonusCredits,
  getPromoCodes,
  getPromoRedemptions,
  createPromoCode,
  updatePromoCode,
  getAdminActions,
  getActivityFeed,
  getUsersWithStatus,
  getUserTimeline,
  updateUserStatus,
  getIPAnalytics,
  updateIPControl,
  getDomainAnalytics,
  updateDomainControl,
  getGenerations,
  getUserGenerations,
  getUserResources,
  getUserCreditLedger,
  getUserPayments,
  adjustUserCredits,
  getImageUrl,
  getThumbnailUrl,
  resolveStorageImageUrl,
  cleanupStuckGenerations,
  getStuckGenerationsCount,
  getSalesMetrics,
  getSalesTransactions,
  recordManualSaleByEmail,
  getEditors,
  getDefaultEditors,
  addEditor,
  removeEditor,
  setDefaultEditors,
  createEditorUser,
  getAdminImageMotionProjects,
  getAdminTours,
  getAdminImageMotionPlayback,
  supabase,
  type EditorRow,
  type DefaultEditorRow,
  type AdminImageMotionProject,
  type AdminTourRow,
} from '../services/supabase';

interface AdminPanelProps {
  user: UserProfile;
  adminRole: 'admin' | 'superadmin' | 'editor';
  onClose: () => void;
}

type AdminTab = 'orders' | 'editors' | 'activity' | 'users' | 'signups' | 'ips' | 'domains' | 'promos' | 'audit' | 'generations' | 'sales' | 'subscriptions' | 'imagemotion' | 'tours' | 'shareDemos' | 'aiTransparency';
type UserDetailTab = 'timeline' | 'generations' | 'credits' | 'payments';

// URL slug for each admin section so every page has its own shareable URL
// (e.g. /admin/users). Kept separate from the tab key so the slugs stay tidy.
const ADMIN_TAB_TO_SLUG: Record<AdminTab, string> = {
  orders: 'orders',
  editors: 'editors',
  activity: 'activity',
  users: 'users',
  signups: 'signups',
  ips: 'ips',
  domains: 'domains',
  promos: 'promos',
  audit: 'audit',
  generations: 'generations',
  sales: 'sales',
  subscriptions: 'subscriptions',
  imagemotion: 'imagemotion',
  tours: 'tours',
  shareDemos: 'share-demos',
  aiTransparency: 'ai-transparency',
};

const ADMIN_SLUG_TO_TAB: Record<string, AdminTab> = Object.entries(ADMIN_TAB_TO_SLUG)
  .reduce((acc, [tab, slug]) => {
    acc[slug] = tab as AdminTab;
    return acc;
  }, {} as Record<string, AdminTab>);

function parseUserDetailTab(value: string | null): UserDetailTab | null {
  if (value === 'timeline' || value === 'generations' || value === 'credits' || value === 'payments') {
    return value;
  }
  return null;
}

function parseAdminLocation(pathname: string, search = ''): {
  tab: AdminTab | null;
  userId: string | null;
  detailTab: UserDetailTab | null;
} {
  const match = pathname.match(
    /^\/admin\/([a-zA-Z-]+)(?:\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}))?\/?$/
  );
  if (!match) return { tab: null, userId: null, detailTab: null };
  const tab = ADMIN_SLUG_TO_TAB[match[1].toLowerCase()] ?? null;
  const params = new URLSearchParams(search);
  return {
    tab,
    userId: tab === 'users' ? (match[2] ?? null) : null,
    detailTab: parseUserDetailTab(params.get('tab')),
  };
}

function adminUserPath(userId: string, detailTab: UserDetailTab = 'timeline'): string {
  const base = `/admin/users/${userId}`;
  return detailTab === 'timeline' ? base : `${base}?tab=${detailTab}`;
}

function formatAdminMoney(cents: number | null | undefined, currency: string | null | undefined): string {
  if (cents == null) return '—';
  const code = (currency || 'eur').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${code}`;
  }
}

// Pro orders (shared with sketchplan)
interface ProOrder {
  id: string;
  job_number: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
  property_address: string;
  estimated_sq_ft: number | null;
  property_type: string;
  floors: number;
  turnaround: string;
  notes: string | null;
  sketch_url: string;
  contact_email: string;
  contact_phone: string | null;
  quoted_price: number | null;
  final_price: number | null;
  // Mirrors the pro_orders DB CHECK constraint (shared with sketchplan, incl. card-payment flow).
  status: 'pending_payment' | 'pending' | 'quoted' | 'paid' | 'in_progress' | 'completed' | 'cancelled';
  output_url: string | null;
  delivered_at: string | null;
  admin_notes: string | null;
  assigned_editor_id: string | null;
  started_at: string | null;
  due_at: string | null;
}
type OrderStatus = ProOrder['status'];

function getFirstSketchUrl(sketchUrl: string | null | undefined): string | null {
  if (!sketchUrl?.trim()) return null;
  const t = sketchUrl.trim();
  if (t.startsWith('[')) {
    try {
      const arr = JSON.parse(t) as unknown;
      if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'string') return arr[0];
    } catch { /* ignore */ }
  }
  return t;
}

type Badge = { bg: string; text: string; label: string };

const ORDER_STATUS_COLORS: Record<OrderStatus, Badge> = {
  pending_payment: { bg: 'bg-slate-800/60 border-slate-700', text: 'text-slate-400', label: 'Awaiting Payment' },
  pending: { bg: 'bg-amber-900/30 border-amber-800', text: 'text-amber-400', label: 'Pending' },
  quoted: { bg: 'bg-purple-900/30 border-purple-800', text: 'text-purple-400', label: 'Quoted' },
  paid: { bg: 'bg-teal-900/30 border-teal-800', text: 'text-teal-400', label: 'Paid' },
  in_progress: { bg: 'bg-blue-900/30 border-blue-800', text: 'text-blue-400', label: 'In Progress' },
  completed: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'Completed' },
  cancelled: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Cancelled' }
};

// Never crash on a status/key that isn't in a colour map (e.g. a new DB enum value
// added by another app). Falls back to a neutral badge with a humanised label.
function badgeStyle<T extends string>(map: Record<T, Badge>, key: T | string | null | undefined): Badge {
  const found = key != null ? map[key as T] : undefined;
  if (found) return found;
  const label = key ? String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';
  return { bg: 'bg-slate-700 border-slate-600', text: 'text-slate-300', label };
}

const STATUS_COLORS: Record<UserRiskStatus, { bg: string; text: string; label: string }> = {
  ok: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'OK' },
  watch: { bg: 'bg-yellow-900/30 border-yellow-800', text: 'text-yellow-400', label: 'Watch' },
  restricted: { bg: 'bg-orange-900/30 border-orange-800', text: 'text-orange-400', label: 'Restricted' },
  banned: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Banned' }
};

const CONTROL_COLORS: Record<ControlStatus | DomainControlStatus, { bg: string; text: string; label: string }> = {
  ok: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'OK' },
  throttled: { bg: 'bg-yellow-900/30 border-yellow-800', text: 'text-yellow-400', label: 'Throttled' },
  require_payment: { bg: 'bg-orange-900/30 border-orange-800', text: 'text-orange-400', label: 'Require Payment' },
  blocked: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Blocked' }
};

const ADMIN_GENERATION_THUMBNAIL = { width: 140, height: 105, quality: 55, resize: 'cover' as const };
// height 0 is omitted by getThumbnailUrl so the preview keeps the photo's aspect ratio.
const ADMIN_LIGHTBOX_PREVIEW = { width: 1600, height: 0, quality: 75, resize: 'contain' as const };

type AdminLightboxMode = 'original' | 'processed' | 'compare';

type AdminLightboxState = {
  generationId: string;
  inputPath: string | null;
  outputPath: string | null;
  mode: AdminLightboxMode;
  email?: string | null;
  createdAt?: string;
  status?: GenerationStatus | string | null;
  sourceApp?: string | null;
  aiOperation?: string | null;
  creditsUsed?: number;
};

function AdminLightboxPane({
  path,
  label,
  className,
}: {
  path: string | null;
  label: string;
  className?: string;
}) {
  const publicUrl = path ? getImageUrl(path) : null;
  // Screen-sized render. The stored PNG is often 8–20MB; downloading it just to preview stalls the gallery.
  const previewUrl = publicUrl ? getThumbnailUrl(publicUrl, ADMIN_LIGHTBOX_PREVIEW) : null;
  const [url, setUrl] = useState<string | null>(previewUrl);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setUrl(previewUrl);
    setLoaded(false);
    setFailed(false);
  }, [previewUrl]);

  if (!path || !url || failed) {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 bg-slate-900/80 rounded-xl border border-slate-800 ${className || ''}`}>
        <Image size={28} className="text-slate-600" />
        <span className="text-xs text-slate-500">{label} unavailable</span>
      </div>
    );
  }

  return (
    <div className={`relative flex items-center justify-center ${className || ''}`}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 size={28} className="animate-spin text-teal-400" />
        </div>
      )}
      <img
        key={url}
        src={url}
        alt={label}
        className={`max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (publicUrl && url !== publicUrl) {
            setLoaded(false);
            setUrl(publicUrl);
            return;
          }
          setFailed(true);
        }}
      />
      <span className="absolute top-3 left-3 bg-black/70 text-white text-xs font-medium px-2.5 py-1 rounded-md backdrop-blur-sm">
        {label}
      </span>
    </div>
  );
}

function AdminGenerationLightbox({
  state,
  list,
  downloadingId,
  onClose,
  onNavigate,
  onModeChange,
  onDownload,
}: {
  state: AdminLightboxState;
  list: Generation[];
  downloadingId: string | null;
  onClose: () => void;
  onNavigate: (gen: Generation, mode: AdminLightboxMode) => void;
  onModeChange: (mode: AdminLightboxMode) => void;
  onDownload: (path: string | null, id: string) => void;
}) {
  const viewable = list.filter((g) => g.input_asset_path || g.output_image_path);
  const index = viewable.findIndex((g) => g.id === state.generationId);
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < viewable.length - 1;
  const hasOriginal = Boolean(state.inputPath);
  const hasProcessed = Boolean(state.outputPath);
  const canCompare = hasOriginal && hasProcessed;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) {
        const gen = viewable[index - 1];
        onNavigate(gen, state.mode === 'compare' && !(gen.input_asset_path && gen.output_image_path)
          ? (gen.output_image_path ? 'processed' : 'original')
          : state.mode);
      }
      if (e.key === 'ArrowRight' && hasNext) {
        const gen = viewable[index + 1];
        onNavigate(gen, state.mode === 'compare' && !(gen.input_asset_path && gen.output_image_path)
          ? (gen.output_image_path ? 'processed' : 'original')
          : state.mode);
      }
      if (e.key === 'o' || e.key === 'O') {
        if (hasOriginal) onModeChange('original');
      }
      if (e.key === 'p' || e.key === 'P') {
        if (hasProcessed) onModeChange('processed');
      }
      if (e.key === 'c' || e.key === 'C') {
        if (canCompare) onModeChange('compare');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPrev, hasNext, hasOriginal, hasProcessed, canCompare, index, viewable, state.mode, onClose, onNavigate, onModeChange]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const activeDownloadPath =
    state.mode === 'original' ? state.inputPath : state.mode === 'processed' ? state.outputPath : state.outputPath;
  const activeDownloadId =
    state.mode === 'original' ? `${state.generationId}-original` : state.generationId;

  // Resolve AI operation / model info. Falls back to the source app's default
  // operation for legacy generations without an ai_operation in metadata.
  const fallbackOperation =
    APP_DEFAULT_OPERATION[state.sourceApp as keyof typeof APP_DEFAULT_OPERATION] ||
    APP_DEFAULT_OPERATION.photoclear;
  const operationDef = getOperationDefinition(state.aiOperation || fallbackOperation);
  const operationLabel = operationDef?.label || state.aiOperation || null;
  const modelName = operationDef?.model || null;
  const statusInfo = state.status
    ? GENERATION_STATUS_COLORS[state.status as GenerationStatus] || GENERATION_STATUS_COLORS.queued
    : null;

  return (
    <div
      className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white bg-slate-800/80 hover:bg-slate-700 rounded-full transition-colors z-20"
        aria-label="Close"
      >
        <X size={22} />
      </button>

      {hasPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const gen = viewable[index - 1];
            onNavigate(
              gen,
              state.mode === 'compare' && !(gen.input_asset_path && gen.output_image_path)
                ? gen.output_image_path
                  ? 'processed'
                  : 'original'
                : state.mode
            );
          }}
          className="absolute left-3 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-700/80 rounded-full transition-colors z-20"
          aria-label="Previous"
        >
          <ChevronLeft size={26} />
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const gen = viewable[index + 1];
            onNavigate(
              gen,
              state.mode === 'compare' && !(gen.input_asset_path && gen.output_image_path)
                ? gen.output_image_path
                  ? 'processed'
                  : 'original'
                : state.mode
            );
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-700/80 rounded-full transition-colors z-20"
          aria-label="Next"
        >
          <ChevronRight size={26} />
        </button>
      )}

      <div
        className="w-full max-w-6xl flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {index >= 0 && (
          <span className="text-slate-500 text-xs">
            {index + 1} / {viewable.length}
          </span>
        )}

        {state.mode === 'compare' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
            <AdminLightboxPane path={state.inputPath} label="Original" className="min-h-[40vh]" />
            <AdminLightboxPane path={state.outputPath} label="Processed" className="min-h-[40vh]" />
          </div>
        ) : (
          <AdminLightboxPane
            path={state.mode === 'original' ? state.inputPath : state.outputPath}
            label={state.mode === 'original' ? 'Original' : 'Processed'}
          />
        )}

        {/* User details & AI model */}
        <div className="w-full max-w-2xl bg-slate-900/90 border border-slate-800 rounded-xl px-4 py-3">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-sm">
            <span className="flex items-center gap-1.5 text-white font-medium">
              <User size={14} className="text-slate-400" />
              {state.email || 'Unknown user'}
            </span>
            {state.createdAt && (
              <span className="flex items-center gap-1.5 text-slate-400">
                <Clock size={13} className="text-slate-500" />
                {new Date(state.createdAt).toLocaleString()}
              </span>
            )}
            {statusInfo && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                {statusInfo.label}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs mt-2 text-slate-400">
            {state.sourceApp && (
              <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 capitalize">
                {state.sourceApp}
              </span>
            )}
            {operationLabel && (
              <span className="flex items-center gap-1.5">
                <Sparkles size={12} className="text-orange-400" />
                {operationLabel}
              </span>
            )}
            {modelName && (
              <span className="font-mono text-slate-500" title="AI model">
                {modelName}
              </span>
            )}
            {typeof state.creditsUsed === 'number' && state.creditsUsed > 0 && (
              <span className="text-orange-400">{state.creditsUsed} credit{state.creditsUsed === 1 ? '' : 's'}</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {hasOriginal && (
            <button
              type="button"
              onClick={() => onModeChange('original')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                state.mode === 'original' ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Original
            </button>
          )}
          {hasProcessed && (
            <button
              type="button"
              onClick={() => onModeChange('processed')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                state.mode === 'processed' ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Processed
            </button>
          )}
          {canCompare && (
            <button
              type="button"
              onClick={() => onModeChange('compare')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                state.mode === 'compare' ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              Compare
            </button>
          )}
          {activeDownloadPath && state.mode !== 'compare' && (
            <>
              <div className="w-px h-5 bg-slate-700 mx-1" />
              <button
                type="button"
                onClick={() => onDownload(activeDownloadPath, activeDownloadId)}
                disabled={downloadingId === activeDownloadId}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                {downloadingId === activeDownloadId ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
                Download
              </button>
            </>
          )}
          {state.mode === 'compare' && (
            <>
              <div className="w-px h-5 bg-slate-700 mx-1" />
              {hasOriginal && (
                <button
                  type="button"
                  onClick={() => onDownload(state.inputPath, `${state.generationId}-original`)}
                  disabled={downloadingId === `${state.generationId}-original`}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {downloadingId === `${state.generationId}-original` ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                  Original
                </button>
              )}
              {hasProcessed && (
                <button
                  type="button"
                  onClick={() => onDownload(state.outputPath, state.generationId)}
                  disabled={downloadingId === state.generationId}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {downloadingId === state.generationId ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                  Processed
                </button>
              )}
            </>
          )}
        </div>
        <p className="text-[11px] text-slate-600">Esc to close · ← → to browse · O / P / C for view mode</p>
      </div>
    </div>
  );
}

function AdminGenImage({
  path,
  alt,
  status,
  onOpen,
  children,
  thumb,
}: {
  path: string | null;
  alt: string;
  status?: GenerationStatus | string | null;
  onOpen?: () => void;
  children?: React.ReactNode;
  thumb?: typeof ADMIN_GENERATION_THUMBNAIL;
}) {
  const thumbSpec = thumb || ADMIN_GENERATION_THUMBNAIL;
  const publicUrl = path ? getImageUrl(path) : null;
  // Public render URL. Result files are often 8–20MB PNGs; a 300px thumb is a few KB.
  const initialSrc = publicUrl ? getThumbnailUrl(publicUrl, thumbSpec) : null;
  const [src, setSrc] = useState<string | null>(initialSrc);
  const [failed, setFailed] = useState(false);
  const fallbackStep = useRef(0);
  const pathRef = useRef(path);
  pathRef.current = path;

  useEffect(() => {
    fallbackStep.current = 0;
    setFailed(false);
    setSrc(initialSrc);
  }, [initialSrc]);

  const emptyLabel = (() => {
    if (failed) return 'Unavailable';
    if (status === 'queued') return 'Not uploaded yet';
    if (status === 'started') return 'Processing…';
    if (status === 'failed') return 'No image';
    return 'No image';
  })();

  return (
    <div className="relative aspect-[4/3] bg-slate-900">
      {src && !failed ? (
        <button
          type="button"
          onClick={onOpen}
          className="block w-full h-full text-left cursor-zoom-in"
          title="View larger"
        >
          <img
            key={path || alt}
            src={src}
            alt={alt}
            className="w-full h-full object-cover hover:opacity-80 transition-opacity"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => {
              const requestedPath = path;
              const step = fallbackStep.current;
              // Transform missed (private object, or a file the renderer cannot resize).
              // Try a signed thumb before falling back to the original file.
              if (step === 0 && requestedPath) {
                fallbackStep.current = 1;
                resolveStorageImageUrl(requestedPath, thumbSpec).then((signed) => {
                  if (pathRef.current !== requestedPath) return;
                  if (signed && signed !== initialSrc) {
                    setSrc(signed);
                    return;
                  }
                  if (publicUrl && publicUrl !== initialSrc) {
                    fallbackStep.current = 2;
                    setSrc(publicUrl);
                    return;
                  }
                  setFailed(true);
                });
                return;
              }
              if (step <= 1 && publicUrl && src !== publicUrl) {
                fallbackStep.current = 2;
                setSrc(publicUrl);
                return;
              }
              setFailed(true);
            }}
          />
        </button>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-2">
          <Image size={22} className="text-slate-700" />
          <span className="text-[10px] text-slate-600 text-center leading-tight">{emptyLabel}</span>
        </div>
      )}
      {children}
    </div>
  );
}

const EVENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  magic_link_requested: { label: 'Magic Link', color: 'bg-blue-900/30 text-blue-400' },
  login: { label: 'Login', color: 'bg-green-900/30 text-green-400' },
  generation_started: { label: 'Gen Started', color: 'bg-purple-900/30 text-purple-400' },
  generation_completed: { label: 'Gen Complete', color: 'bg-emerald-900/30 text-emerald-400' },
  generation_failed: { label: 'Gen Failed', color: 'bg-red-900/30 text-red-400' },
  credits_granted: { label: 'Credits+', color: 'bg-orange-900/30 text-orange-400' },
  credits_used: { label: 'Credits-', color: 'bg-slate-700 text-slate-400' },
  payment_completed: { label: 'Payment', color: 'bg-green-900/30 text-green-400' },
  promo_redeemed: { label: 'Promo', color: 'bg-pink-900/30 text-pink-400' },
  user_status_changed: { label: 'Status Δ', color: 'bg-yellow-900/30 text-yellow-400' },
  ip_control_changed: { label: 'IP Ctrl', color: 'bg-cyan-900/30 text-cyan-400' },
  domain_control_changed: { label: 'Domain Ctrl', color: 'bg-indigo-900/30 text-indigo-400' }
};

const GENERATION_STATUS_COLORS: Record<GenerationStatus, { bg: string; text: string; label: string }> = {
  queued: { bg: 'bg-slate-700', text: 'text-slate-400', label: 'Queued' },
  started: { bg: 'bg-blue-900/30 border-blue-800', text: 'text-blue-400', label: 'Processing' },
  completed: { bg: 'bg-green-900/30 border-green-800', text: 'text-green-400', label: 'Completed' },
  failed: { bg: 'bg-red-900/30 border-red-800', text: 'text-red-400', label: 'Failed' }
};

const USER_RESOURCE_APP_LABELS: Record<string, string> = {
  photoclear: 'Photoclear',
  staging: 'Staging',
  photoedit: 'PhotoEdit',
  hdrphoto: 'HDR Photo',
  hyperdusk: 'HyperDusk',
  imagemotion: 'ImageMotion',
  tours: '360 Tours',
  brochures: 'Brochures',
  showcase: 'Showcase',
  sketchplan: 'SketchPlan',
  videomotion: 'VideoMotion',
  record: 'Record',
};

const USER_RESOURCE_KIND_LABELS: Record<string, string> = {
  generation: 'Image',
  imagemotion_project: 'Video project',
  tour: 'Tour',
  brochure: 'Brochure',
  showcase: 'Showcase',
  pro_order: 'Floor plan order',
  videomotion_project: 'Video project',
  recording: 'Recording',
};

function RecordManualSaleForm({ onRecorded }: { onRecorded: () => void }) {
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const credits = parseInt(amount, 10);
    if (!email.trim() || !credits || credits <= 0) {
      setMessage({ type: 'error', text: 'Enter customer email and a positive number of credits.' });
      return;
    }
    setMessage(null);
    setSubmitting(true);
    const result = await recordManualSaleByEmail(email.trim(), credits, description.trim() || undefined);
    setSubmitting(false);
    if (result.success) {
      setMessage({ type: 'success', text: `Recorded sale of ${credits} credits. It will appear in "From today".` });
      setEmail('');
      setAmount('');
      setDescription('');
      onRecorded();
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to record sale' });
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
      <p className="text-sm text-slate-400 mb-3">Record a manual sale (e.g. bank transfer) so it shows in Sales &amp; Costs. Use when you already granted credits.</p>
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Customer email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="customer@example.com"
            className="w-48 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:ring-1 focus:ring-teal-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Credits</span>
          <input
            type="number"
            min={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="150"
            className="w-24 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:ring-1 focus:ring-teal-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Description (optional)</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. 150 credits (£112.50)"
            className="w-56 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:ring-1 focus:ring-teal-500"
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-sm font-medium"
        >
          {submitting ? 'Recording…' : 'Record sale'}
        </button>
      </form>
      {message && (
        <p className={`mt-3 text-sm ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}

export default function AdminPanel({ user, adminRole, onClose }: AdminPanelProps) {
  const isSuperAdmin = adminRole === 'superadmin';
  const [activeTab, setActiveTab] = useState<AdminTab>(
    () => parseAdminLocation(window.location.pathname, window.location.search).tab ?? 'orders'
  );

  // Pro orders & editors
  const [orders, setOrders] = useState<ProOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ProOrder | null>(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatus | ''>('');
  const [updatingOrder, setUpdatingOrder] = useState(false);
  const [uploadingOutput, setUploadingOutput] = useState(false);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [sketchLoadError, setSketchLoadError] = useState(false);
  const [sketchDownloadUrl, setSketchDownloadUrl] = useState<string | null>(null);
  const [editors, setEditors] = useState<EditorRow[]>([]);
  const [defaultEditors, setDefaultEditors] = useState<DefaultEditorRow[]>([]);
  const [loadingEditors, setLoadingEditors] = useState(false);
  const [newEditorUserId, setNewEditorUserId] = useState('');
  const [newEditorEmail, setNewEditorEmail] = useState('');
  const [newEditorPassword, setNewEditorPassword] = useState('');
  const [addingEditor, setAddingEditor] = useState(false);
  const [creatingEditor, setCreatingEditor] = useState(false);
  
  // User Search & Grant Credits
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantMessage, setGrantMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Promo Codes
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [newPromoCredits, setNewPromoCredits] = useState('');
  const [newPromoMaxUses, setNewPromoMaxUses] = useState('');
  const [newPromoExpires, setNewPromoExpires] = useState('');
  const [newPromoDescription, setNewPromoDescription] = useState('');
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
  const [editPromoCredits, setEditPromoCredits] = useState('');
  const [editPromoMaxUses, setEditPromoMaxUses] = useState('');
  const [editPromoExpires, setEditPromoExpires] = useState('');
  const [editPromoDescription, setEditPromoDescription] = useState('');
  const [editPromoActive, setEditPromoActive] = useState(true);
  const [updatingPromo, setUpdatingPromo] = useState(false);

  // Audit Log
  const [auditLog, setAuditLog] = useState<AdminAction[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  
  // Promo Redemptions
  const [redemptions, setRedemptions] = useState<PromoRedemption[]>([]);

  // Activity Feed
  const [activityEvents, setActivityEvents] = useState<EventLog[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityFilter, setActivityFilter] = useState<string>('');
  const [activityAppFilter, setActivityAppFilter] = useState<string>(''); // '' | photoclear | staging | sketchplan
  const [selectedEvent, setSelectedEvent] = useState<EventLog | null>(null);

  // Enhanced Users
  const USERS_PAGE_SIZE = 50;
  const [usersWithStatus, setUsersWithStatus] = useState<UserWithStatus[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [userStatusFilter, setUserStatusFilter] = useState<UserRiskStatus | ''>('');
  const [selectedUserDetail, setSelectedUserDetail] = useState<UserTimeline | null>(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [statusChangeUser, setStatusChangeUser] = useState<UserWithStatus | null>(null);
  const [newStatus, setNewStatus] = useState<UserRiskStatus>('ok');
  const [statusReason, setStatusReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // IP Analytics
  const [ipAnalytics, setIpAnalytics] = useState<IPAnalytics[]>([]);
  const [loadingIPs, setLoadingIPs] = useState(false);
  const [ipError, setIpError] = useState<string | null>(null);
  const [ipDays, setIpDays] = useState(30);
  const [selectedIP, setSelectedIP] = useState<IPAnalytics | null>(null);
  const [ipControlStatus, setIpControlStatus] = useState<ControlStatus>('ok');
  const [ipControlReason, setIpControlReason] = useState('');
  const [ipThrottle, setIpThrottle] = useState('1.0');
  const [updatingIP, setUpdatingIP] = useState(false);
  const [ipControlError, setIpControlError] = useState<string | null>(null);

  // Domain Analytics
  const [domainAnalytics, setDomainAnalytics] = useState<DomainAnalytics[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<DomainAnalytics | null>(null);
  const [domainControlStatus, setDomainControlStatus] = useState<DomainControlStatus>('ok');
  const [domainControlReason, setDomainControlReason] = useState('');
  const [domainThrottle, setDomainThrottle] = useState('1.0');
  const [updatingDomain, setUpdatingDomain] = useState(false);

  // Generations
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [loadingGenerations, setLoadingGenerations] = useState(false);
  const [generationsTotal, setGenerationsTotal] = useState(0);
  const [generationsPage, setGenerationsPage] = useState(0);
  const [generationStatusFilter, setGenerationStatusFilter] = useState<GenerationStatus | ''>('completed');
  const [generationEmailFilter, setGenerationEmailFilter] = useState('');
  const [generationDateFrom, setGenerationDateFrom] = useState('');
  const [generationDateTo, setGenerationDateTo] = useState('');
  const [generationGridCols, setGenerationGridCols] = useState<number>(() => {
    const saved = Number(localStorage.getItem('admin_generation_grid_cols'));
    return saved >= 1 && saved <= 8 ? saved : 3;
  });

  const updateGenerationGridCols = (cols: number) => {
    const clamped = Math.min(8, Math.max(1, cols));
    setGenerationGridCols(clamped);
    localStorage.setItem('admin_generation_grid_cols', String(clamped));
  };

  // Larger thumbnails when fewer columns are shown, so images stay sharp.
  const generationThumb = React.useMemo(() => {
    const width =
      generationGridCols <= 1 ? 720 : generationGridCols <= 2 ? 480 : generationGridCols <= 4 ? 300 : 150;
    return { width, height: Math.round(width * 0.75), quality: 55, resize: 'cover' as const };
  }, [generationGridCols]);

  // User Detail - Generations & Credit Ledger
  const [userGenerations, setUserGenerations] = useState<Generation[]>([]);
  const [userResources, setUserResources] = useState<UserResource[]>([]);
  const [userResourcesTotal, setUserResourcesTotal] = useState(0);
  const [userResourceAppFilter, setUserResourceAppFilter] = useState<string>('');
  const [loadingUserGenerations, setLoadingUserGenerations] = useState(false);
  const [userCreditLedger, setUserCreditLedger] = useState<CreditLedgerEntry[]>([]);
  const [userCreditBalance, setUserCreditBalance] = useState<number>(0);
  const [userCreditLedgerTotal, setUserCreditLedgerTotal] = useState(0);
  const [loadingCreditLedger, setLoadingCreditLedger] = useState(false);
  const [adjustCreditsAmount, setAdjustCreditsAmount] = useState('');
  const [adjustCreditsReason, setAdjustCreditsReason] = useState('');
  const [adjustingCredits, setAdjustingCredits] = useState(false);
  const [showAdjustCredits, setShowAdjustCredits] = useState(false);
  const [userDetailTab, setUserDetailTab] = useState<UserDetailTab>(
    () => parseAdminLocation(window.location.pathname, window.location.search).detailTab ?? 'timeline'
  );
  const [routeUserId, setRouteUserId] = useState<string | null>(
    () => parseAdminLocation(window.location.pathname, window.location.search).userId
  );
  const [copiedUserId, setCopiedUserId] = useState<string | null>(null);
  const [userPayments, setUserPayments] = useState<UserPaymentRow[]>([]);
  const [userCost, setUserCost] = useState<UserCostSummary | null>(null);
  const [loadingUserPayments, setLoadingUserPayments] = useState(false);

  // Stuck generations cleanup
  const [stuckCount, setStuckCount] = useState(0);
  const [cleaningStuck, setCleaningStuck] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{ cleaned: number; refunded: number } | null>(null);

  // Sales Metrics
  const SALES_TX_PAGE_SIZE = 50;
  const [salesMetrics, setSalesMetrics] = useState<SalesMetrics | null>(null);
  const [loadingSales, setLoadingSales] = useState(false);
  const [salesPeriod, setSalesPeriod] = useState<SalesPeriod>('today');
  const [salesTransactions, setSalesTransactions] = useState<SalesTransaction[]>([]);
  const [salesTransactionsTotal, setSalesTransactionsTotal] = useState(0);
  const [salesTransactionsPage, setSalesTransactionsPage] = useState(0);
  const [loadingSalesTransactions, setLoadingSalesTransactions] = useState(false);
  const [salesSearchInput, setSalesSearchInput] = useState('');
  const [salesSearch, setSalesSearch] = useState('');

  // ImageMotion
  const [imProjects, setImProjects] = useState<AdminImageMotionProject[]>([]);
  const [loadingImProjects, setLoadingImProjects] = useState(false);
  const [imStatusFilter, setImStatusFilter] = useState<string>('');
  const [loadingVideoId, setLoadingVideoId] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<{
    title: string;
    items: { label: string; url: string }[];
    active: number;
  } | null>(null);

  // 360 Tours
  const [adminTours, setAdminTours] = useState<AdminTourRow[]>([]);
  const [loadingAdminTours, setLoadingAdminTours] = useState(false);
  const [tourStatusFilter, setTourStatusFilter] = useState<string>('');

  // Image download
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [generationLightbox, setGenerationLightbox] = useState<AdminLightboxState | null>(null);
  const [generationLightboxList, setGenerationLightboxList] = useState<Generation[]>([]);

  const openGenerationLightbox = (
    gen: Generation,
    mode: AdminLightboxMode,
    list: Generation[]
  ) => {
    const hasOriginal = Boolean(gen.input_asset_path);
    const hasProcessed = Boolean(gen.output_image_path);
    if (!hasOriginal && !hasProcessed) return;
    let nextMode = mode;
    if (mode === 'original' && !hasOriginal) nextMode = hasProcessed ? 'processed' : 'original';
    if (mode === 'processed' && !hasProcessed) nextMode = hasOriginal ? 'original' : 'processed';
    if (mode === 'compare' && !(hasOriginal && hasProcessed)) {
      nextMode = hasProcessed ? 'processed' : 'original';
    }
    setGenerationLightboxList(list);
    setGenerationLightbox({
      generationId: gen.id,
      inputPath: gen.input_asset_path,
      outputPath: gen.output_image_path,
      mode: nextMode,
      email: gen.user_email,
      createdAt: gen.created_at,
      status: gen.status,
      sourceApp: (gen.metadata as Record<string, string> | null)?.source_app || null,
      aiOperation: (gen.metadata as Record<string, string> | null)?.ai_operation || null,
      creditsUsed: gen.credits_used,
    });
  };

  // Handle download
  const handleDownload = async (imageUrl: string | null, id: string) => {
    if (!imageUrl) return;
    setDownloadingId(id);
    try {
      const resolved = (await resolveStorageImageUrl(imageUrl)) || imageUrl;
      const response = await fetch(resolved);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generation-${id.substring(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloadingId(null);
  };

  const openImageMotionVideo = async (
    id: string,
    title: string,
    finalPath: string | null,
    finalUrl: string | null,
  ) => {
    setLoadingVideoId(id);
    try {
      const items = await getAdminImageMotionPlayback(id, finalPath, finalUrl);
      setVideoPreview({ title, items, active: 0 });
    } finally {
      setLoadingVideoId(null);
    }
  };

  // Resolve sketch URL for selected order (for download/preview)
  useEffect(() => {
    setSketchLoadError(false);
  }, [selectedOrder?.id, selectedOrder?.sketch_url]);

  useEffect(() => {
    const resolveSketchUrl = async () => {
      const raw = selectedOrder?.sketch_url;
      if (!raw) { setSketchDownloadUrl(null); return; }
      const url = getFirstSketchUrl(raw);
      if (!url) { setSketchDownloadUrl(null); return; }
      if (url.startsWith('blob:')) { setSketchDownloadUrl(url); return; }
      let storagePath: string | null = null;
      const splitToken = '/images/';
      if (url.includes(splitToken)) storagePath = url.split(splitToken)[1]?.split('?')[0] || null;
      if (!storagePath) { setSketchDownloadUrl(url); return; }
      try {
        const { supabase } = await import('../services/supabase');
        const { data, error } = await supabase.storage.from('images').createSignedUrl(storagePath, 60 * 60);
        if (error || !data?.signedUrl) setSketchDownloadUrl(url);
        else setSketchDownloadUrl(data.signedUrl);
      } catch {
        setSketchDownloadUrl(url);
      }
    };
    resolveSketchUrl();
  }, [selectedOrder?.id, selectedOrder?.sketch_url]);

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const { supabase } = await import('../services/supabase');
      let query = supabase.from('pro_orders').select('*').order('created_at', { ascending: false });
      if (orderStatusFilter) query = query.eq('status', orderStatusFilter);
      const { data, error } = await query.limit(100);
      if (!error) setOrders(data || []);
    } catch (err) {
      console.error('Error loading orders:', err);
    }
    setLoadingOrders(false);
  };

  const updateOrderStatus = async (orderId: string, status: OrderStatus) => {
    setUpdatingOrder(true);
    try {
      const { supabase } = await import('../services/supabase');
      const updates: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
      if (status === 'in_progress') updates.started_at = new Date().toISOString();
      else if (status === 'completed') updates.delivered_at = new Date().toISOString();
      const { error } = await supabase.from('pro_orders').update(updates).eq('id', orderId);
      if (!error) {
        loadOrders();
        if (selectedOrder?.id === orderId) setSelectedOrder({ ...selectedOrder, status, ...updates } as ProOrder);
      }
    } catch (err) {
      console.error('Error updating order:', err);
    }
    setUpdatingOrder(false);
  };

  const uploadOutputFile = async (orderId: string, file: File) => {
    setUploadingOutput(true);
    try {
      const { supabase, uploadImageToStorage } = await import('../services/supabase');
      const url = await uploadImageToStorage(orderId, file, 'output', orderId);
      if (url) {
        const { error } = await supabase.from('pro_orders').update({
          output_url: url, status: 'completed', delivered_at: new Date().toISOString(), updated_at: new Date().toISOString()
        }).eq('id', orderId);
        if (!error) {
          loadOrders();
          if (selectedOrder?.id === orderId) setSelectedOrder({ ...selectedOrder, output_url: url, status: 'completed' });
        }
      }
    } catch (err) {
      console.error('Error uploading output:', err);
    }
    setUploadingOutput(false);
  };

  const sendDeliveryEmail = async (order: ProOrder) => {
    if (!order.output_url) { alert('Please upload the completed floor plan first.'); return; }
    try {
      const { supabase } = await import('../services/supabase');
      await supabase.functions.invoke('notify-delivery', {
        body: { order_id: order.id, contact_email: order.contact_email, property_address: order.property_address, output_url: order.output_url },
      });
      alert('Delivery email sent to ' + order.contact_email);
    } catch (err) {
      console.error('Error sending delivery email:', err);
      alert('Failed to send email. Please send manually.');
    }
  };

  const deleteOrder = async (orderId: string) => {
    if (!isSuperAdmin) { alert('Only superadmins can delete orders'); return; }
    if (!window.confirm('Delete this order permanently? This cannot be undone.')) return;
    setDeletingOrderId(orderId);
    try {
      const { supabase } = await import('../services/supabase');
      const { data, error } = await supabase.from('pro_orders').delete().eq('id', orderId).select();
      if (error) { alert(`Delete failed: ${error.message}`); return; }
      if (!data?.length) { alert('Delete failed - no rows affected.'); return; }
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      if (selectedOrder?.id === orderId) setSelectedOrder(null);
    } catch (err: any) {
      alert(`Failed to delete: ${err?.message}`);
    }
    setDeletingOrderId(null);
  };

  const loadEditorsData = async () => {
    setLoadingEditors(true);
    try {
      const [e, d] = await Promise.all([getEditors(), getDefaultEditors()]);
      setEditors(e);
      setDefaultEditors(d);
    } catch (err) {
      console.error('Load editors error:', err);
    }
    setLoadingEditors(false);
  };

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'orders') {
      loadOrders();
    } else if (activeTab === 'editors') {
      loadEditorsData();
    } else if (activeTab === 'activity') {
      loadActivityFeed();
    } else if (activeTab === 'users') {
      loadUsersWithStatus();
    } else if (activeTab === 'ips') {
      loadIPAnalytics();
    } else if (activeTab === 'domains') {
      loadDomainAnalytics();
    } else if (activeTab === 'promos') {
      loadPromoCodes();
      loadRedemptions();
    } else if (activeTab === 'audit') {
      loadAuditLog();
    } else if (activeTab === 'generations') {
      loadGenerations();
    } else if (activeTab === 'sales') {
      loadSalesMetrics();
      loadSalesTransactions();
    } else if (activeTab === 'imagemotion') {
      loadImProjects();
    } else if (activeTab === 'tours') {
      loadAdminTours();
    }
  }, [activeTab]);

  useEffect(() => {
    if (adminRole === 'editor' && activeTab !== 'orders') setActiveTab('orders');
  }, [adminRole]);

  // Reflect the active section in the URL so each admin page, and each user,
  // is its own shareable URL (/admin/users/<id>). The first sync uses
  // replaceState; later section changes use pushState so Back steps through
  // them. Switching tabs on the same user only replaces the query string.
  const didInitAdminUrlRef = useRef(false);
  const prevAdminPathRef = useRef<string | null>(null);
  const requestedUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const userIdForUrl = activeTab === 'users' ? routeUserId : null;
    const desiredPath = userIdForUrl
      ? adminUserPath(userIdForUrl, userDetailTab)
      : `/admin/${ADMIN_TAB_TO_SLUG[activeTab]}`;
    const current = `${window.location.pathname}${window.location.search}`;
    if (current !== desiredPath) {
      const samePage = prevAdminPathRef.current?.split('?')[0] === desiredPath.split('?')[0];
      if (!didInitAdminUrlRef.current || samePage) {
        window.history.replaceState(window.history.state, '', desiredPath);
      } else {
        window.history.pushState(window.history.state, '', desiredPath);
      }
    }
    prevAdminPathRef.current = desiredPath;
    didInitAdminUrlRef.current = true;
  }, [activeTab, routeUserId, userDetailTab]);

  // Sync the active tab when the user navigates with the browser back/forward
  // buttons between admin sections.
  useEffect(() => {
    const handlePopState = () => {
      const parsed = parseAdminLocation(window.location.pathname, window.location.search);
      if (parsed.tab) setActiveTab(parsed.tab);
      if (parsed.tab === 'users' && parsed.userId) {
        setRouteUserId(parsed.userId);
        setUserDetailTab(parsed.detailTab ?? 'timeline');
      } else if (!parsed.userId) {
        requestedUserIdRef.current = null;
        setRouteUserId(null);
        setSelectedUserDetail(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const salesDateFrom = (period: SalesPeriod = salesPeriod) => {
    const now = new Date();
    if (period === 'today') {
      const startOfToday = new Date(now);
      startOfToday.setHours(0, 0, 0, 0);
      return startOfToday.toISOString();
    }
    if (period === '7d') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    if (period === '30d') return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    return undefined;
  };

  const loadSalesMetrics = async (period: SalesPeriod = salesPeriod) => {
    setLoadingSales(true);
    const result = await getSalesMetrics(salesDateFrom(period));
    if (result.success && result.metrics) {
      setSalesMetrics(result.metrics);
    }
    setLoadingSales(false);
  };

  const loadSalesTransactions = async (
    period: SalesPeriod = salesPeriod,
    page: number = salesTransactionsPage,
    search: string = salesSearch
  ) => {
    setLoadingSalesTransactions(true);
    const result = await getSalesTransactions(
      salesDateFrom(period),
      undefined,
      SALES_TX_PAGE_SIZE,
      page * SALES_TX_PAGE_SIZE,
      search.trim() || undefined
    );
    if (result.success) {
      setSalesTransactions(result.transactions || []);
      setSalesTransactionsTotal(result.total || 0);
    }
    setLoadingSalesTransactions(false);
  };

  const applySalesPeriod = (period: SalesPeriod) => {
    setSalesPeriod(period);
    setSalesTransactionsPage(0);
    loadSalesMetrics(period);
    loadSalesTransactions(period, 0);
  };

  const applySalesSearch = (value: string = salesSearchInput) => {
    const next = value.trim();
    setSalesSearch(next);
    setSalesTransactionsPage(0);
    loadSalesTransactions(salesPeriod, 0, next);
  };

  const loadImProjects = async () => {
    setLoadingImProjects(true);
    try {
      const data = await getAdminImageMotionProjects();
      setImProjects(data);
    } catch (e) {
      console.error('Failed to load imagemotion projects:', e);
    }
    setLoadingImProjects(false);
  };

  const loadAdminTours = async () => {
    setLoadingAdminTours(true);
    try {
      const data = await getAdminTours();
      setAdminTours(data);
    } catch (e) {
      console.error('Failed to load admin tours:', e);
    }
    setLoadingAdminTours(false);
  };

  const loadPromoCodes = async () => {
    setLoadingPromos(true);
    const data = await getPromoCodes();
    setPromoCodes(data);
    setLoadingPromos(false);
  };

  const loadRedemptions = async () => {
    const data = await getPromoRedemptions();
    setRedemptions(data);
  };

  const loadAuditLog = async () => {
    setLoadingAudit(true);
    const data = await getAdminActions();
    setAuditLog(data);
    setLoadingAudit(false);
  };

  const loadActivityFeed = async () => {
    setLoadingActivity(true);
    const result = await getActivityFeed(100, 0, activityFilter || undefined);
    if (result.success && result.events) {
      setActivityEvents(result.events);
      setActivityTotal(result.total || 0);
    }
    setLoadingActivity(false);
  };

  const loadUsersWithStatus = async (
    page: number = usersPage,
    statusFilter: UserRiskStatus | '' = userStatusFilter
  ) => {
    setLoadingUsers(true);
    const result = await getUsersWithStatus(
      USERS_PAGE_SIZE,
      page * USERS_PAGE_SIZE,
      searchTerm || undefined,
      statusFilter || undefined
    );
    if (result.success && result.users) {
      setUsersWithStatus(result.users);
      setUsersTotal(result.total || 0);
    }
    setLoadingUsers(false);
  };

  const searchUsersWithStatus = () => {
    setUsersPage(0);
    loadUsersWithStatus(0);
  };

  const loadUserDetail = async (userId: string) => {
    requestedUserIdRef.current = userId;
    setLoadingUserDetail(true);
    const result = await getUserTimeline(userId);
    if (requestedUserIdRef.current !== userId) return;
    if (result.success && result.user) {
      setSelectedUserDetail({ user: result.user, events: result.events || [] });
      loadUserGenerationsAndLedger(userId);
    }
    setLoadingUserDetail(false);
  };

  const loadedUserRouteRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeTab !== 'users' || !routeUserId) {
      loadedUserRouteRef.current = null;
      return;
    }
    if (loadedUserRouteRef.current === routeUserId) return;
    loadedUserRouteRef.current = routeUserId;
    void loadUserDetail(routeUserId);
  }, [activeTab, routeUserId]);

  const copyUserUrl = async (userId: string, detailTab: UserDetailTab = 'timeline') => {
    const url = `${window.location.origin}${adminUserPath(userId, detailTab)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUserId(userId);
      window.setTimeout(() => setCopiedUserId((current) => (current === userId ? null : current)), 1500);
    } catch {
      setCopiedUserId(null);
    }
  };

  const handleUpdateUserStatus = async () => {
    if (!statusChangeUser) return;
    setUpdatingStatus(true);
    const result = await updateUserStatus(statusChangeUser.id, newStatus, statusReason || undefined);
    if (result.success) {
      await loadUsersWithStatus();
      setStatusChangeUser(null);
      setStatusReason('');
    }
    setUpdatingStatus(false);
  };

  const loadIPAnalytics = async (days?: number) => {
    setLoadingIPs(true);
    setIpError(null);
    const result = await getIPAnalytics(50, days ?? ipDays);
    if (result.success && result.ips) {
      setIpAnalytics(result.ips);
    } else if (!result.success) {
      setIpError(result.error || 'Unknown error loading IP analytics');
      console.error('IP Analytics error:', result);
    }
    setLoadingIPs(false);
  };

  const handleUpdateIPControl = async () => {
    if (!selectedIP) return;
    setUpdatingIP(true);
    setIpControlError(null);
    const result = await updateIPControl(
      selectedIP.ip_address,
      ipControlStatus,
      parseFloat(ipThrottle) || 1.0,
      ipControlReason || undefined
    );
    if (result?.success) {
      await loadIPAnalytics();
      setSelectedIP(null);
      setIpControlReason('');
      setIpControlError(null);
    } else {
      setIpControlError(result?.error || 'Could not save this IP block.');
    }
    setUpdatingIP(false);
  };

  const loadDomainAnalytics = async () => {
    setLoadingDomains(true);
    const result = await getDomainAnalytics(50);
    if (result.success && result.domains) {
      setDomainAnalytics(result.domains);
    }
    setLoadingDomains(false);
  };

  const loadGenerations = async () => {
    setLoadingGenerations(true);
    const result = await getGenerations({
      limit: 12,
      offset: generationsPage * 12,
      status: generationStatusFilter || undefined,
      userEmail: generationEmailFilter || undefined,
      dateFrom: generationDateFrom || undefined,
      dateTo: generationDateTo || undefined
    });
    if (result.success && result.generations) {
      setGenerations(result.generations);
      setGenerationsTotal(result.total || 0);
    }
    setLoadingGenerations(false);
    // Also load stuck count
    loadStuckCount();
  };

  const loadStuckCount = async () => {
    const count = await getStuckGenerationsCount();
    setStuckCount(count);
  };

  const handleCleanupStuck = async () => {
    setCleaningStuck(true);
    setCleanupResult(null);
    const result = await cleanupStuckGenerations();
    if (result.success) {
      setCleanupResult({ 
        cleaned: result.generations_cleaned || 0, 
        refunded: result.credits_refunded || 0 
      });
      // Refresh generations and stuck count
      await loadGenerations();
    }
    setCleaningStuck(false);
  };

  const loadUserGenerationsAndLedger = async (userId: string) => {
    setLoadingUserGenerations(true);
    setLoadingCreditLedger(true);
    setLoadingUserPayments(true);
    setUserResourceAppFilter('');

    const [genResult, resourcesResult, ledgerResult, paymentsResult] = await Promise.all([
      getUserGenerations(userId, 50),
      getUserResources(userId, 100),
      getUserCreditLedger(userId, 400),
      getUserPayments(userId, 100)
    ]);

    if (genResult.success && genResult.generations) {
      setUserGenerations(genResult.generations);
    } else {
      setUserGenerations([]);
    }
    if (resourcesResult.success && resourcesResult.resources) {
      setUserResources(resourcesResult.resources);
      setUserResourcesTotal(resourcesResult.total ?? resourcesResult.resources.length);
    } else {
      // Fallback: show generations-only if the new RPC is not deployed yet
      const fallback: UserResource[] = (genResult.generations || []).map((g) => ({
        id: g.id,
        kind: 'generation' as const,
        app: (g.metadata as Record<string, string> | null)?.source_app || 'photoclear',
        title: (g.metadata as Record<string, string> | null)?.mode || 'Generation',
        status: g.status,
        created_at: g.created_at,
        credits_used: g.credits_used,
        preview_path: g.input_asset_path,
        output_path: g.output_image_path,
        link_url: null,
        meta: {
          input_asset_path: g.input_asset_path,
          output_image_path: g.output_image_path,
          error_message: g.error_message,
          metadata: g.metadata,
        },
      }));
      setUserResources(fallback);
      setUserResourcesTotal(fallback.length);
    }
    if (ledgerResult.success) {
      setUserCreditLedger(ledgerResult.entries || []);
      setUserCreditBalance(ledgerResult.balance ?? 0);
      setUserCreditLedgerTotal(ledgerResult.total ?? ledgerResult.entries?.length ?? 0);
    } else {
      setUserCreditLedger([]);
      setUserCreditLedgerTotal(0);
    }
    if (paymentsResult.success) {
      setUserPayments(paymentsResult.payments || []);
      setUserCost(paymentsResult.summary ?? null);
    } else {
      setUserPayments([]);
      setUserCost(null);
    }

    setLoadingUserGenerations(false);
    setLoadingCreditLedger(false);
    setLoadingUserPayments(false);
  };

  const handleAdjustCredits = async () => {
    if (!selectedUserDetail || !adjustCreditsAmount || !adjustCreditsReason.trim()) return;
    
    setAdjustingCredits(true);
    const result = await adjustUserCredits(
      selectedUserDetail.user.id,
      parseInt(adjustCreditsAmount),
      adjustCreditsReason
    );
    
    if (result.success) {
      // Refresh user data
      await loadUserGenerationsAndLedger(selectedUserDetail.user.id);
      await loadUserDetail(selectedUserDetail.user.id);
      setShowAdjustCredits(false);
      setAdjustCreditsAmount('');
      setAdjustCreditsReason('');
    }
    setAdjustingCredits(false);
  };

  const handleUpdateDomainControl = async () => {
    if (!selectedDomain) return;
    setUpdatingDomain(true);
    const result = await updateDomainControl(
      selectedDomain.domain,
      domainControlStatus,
      parseFloat(domainThrottle) || 1.0,
      domainControlReason || undefined
    );
    if (result.success) {
      await loadDomainAnalytics();
      setSelectedDomain(null);
      setDomainControlReason('');
    }
    setUpdatingDomain(false);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      return;
    }

    setSearching(true);
    try {
      const results = await searchUsers(searchTerm);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleGrantCredits = async () => {
    if (!selectedUser || !creditAmount || parseInt(creditAmount) <= 0) {
      setGrantMessage({ type: 'error', text: 'Please select a user and enter a valid credit amount' });
      return;
    }

    if (!creditReason.trim()) {
      setGrantMessage({ type: 'error', text: 'Please provide a reason for this credit grant' });
      return;
    }

    setGranting(true);
    setGrantMessage(null);

    try {
      const result = await grantBonusCredits(
        selectedUser.id,
        parseInt(creditAmount),
        creditReason
      );

      if (result.success) {
        setGrantMessage({ 
          type: 'success', 
          text: `Successfully granted ${creditAmount} credits to ${selectedUser.email}` 
        });
        setCreditAmount('');
        setCreditReason('');
        setSelectedUser(null);
        setSearchResults([]);
        setSearchTerm('');
      } else {
        setGrantMessage({ type: 'error', text: result.error || 'Failed to grant credits' });
      }
    } catch (err: any) {
      setGrantMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setGranting(false);
    }
  };

  const handleCreatePromo = async () => {
    if (!newPromoCode.trim() || !newPromoCredits || parseInt(newPromoCredits) <= 0) {
      setPromoMessage({ type: 'error', text: 'Code and credits are required' });
      return;
    }

    setCreatingPromo(true);
    setPromoMessage(null);

    try {
      const result = await createPromoCode(
        newPromoCode.trim(),
        parseInt(newPromoCredits),
        newPromoMaxUses ? parseInt(newPromoMaxUses) : null,
        newPromoExpires || null,
        newPromoDescription || null
      );

      if (result.success) {
        setPromoMessage({ type: 'success', text: `Promo code "${result.promo_id}" created successfully` });
        setNewPromoCode('');
        setNewPromoCredits('');
        setNewPromoMaxUses('');
        setNewPromoExpires('');
        setNewPromoDescription('');
        setShowCreatePromo(false);
        await loadPromoCodes();
      } else {
        const errorMessages: Record<string, string> = {
          'INVALID_CODE_LENGTH': 'Code must be 3-20 characters',
          'INVALID_CODE_FORMAT': 'Code must be alphanumeric (A-Z, 0-9)',
          'CODE_EXISTS': 'This code already exists',
          'NOT_AUTHORIZED': 'You are not authorized to create promo codes'
        };
        setPromoMessage({ type: 'error', text: errorMessages[result.error || ''] || result.error || 'Failed to create code' });
      }
    } catch (err: any) {
      setPromoMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleTogglePromo = async (promoId: string, currentActive: boolean) => {
    try {
      const result = await updatePromoCode(promoId, !currentActive);
      if (result.success) {
        await loadPromoCodes();
      }
    } catch (err) {
      console.error('Toggle promo error:', err);
    }
  };

  const handleStartEditPromo = (promo: PromoCode) => {
    setEditingPromoId(promo.id);
    setEditPromoCredits(promo.credits.toString());
    setEditPromoMaxUses(promo.max_uses?.toString() || '');
    setEditPromoExpires(promo.expires_at ? promo.expires_at.split('T')[0] : '');
    setEditPromoDescription(promo.description || '');
    setEditPromoActive(promo.active);
    setPromoMessage(null);
  };

  const handleCancelEditPromo = () => {
    setEditingPromoId(null);
    setEditPromoCredits('');
    setEditPromoMaxUses('');
    setEditPromoExpires('');
    setEditPromoDescription('');
    setEditPromoActive(true);
    setPromoMessage(null);
  };

  const handleUpdatePromo = async (promoId: string) => {
    if (!editPromoCredits || parseInt(editPromoCredits) <= 0) {
      setPromoMessage({ type: 'error', text: 'Credits must be greater than 0' });
      return;
    }

    setUpdatingPromo(true);
    setPromoMessage(null);

    try {
      const result = await updatePromoCode(
        promoId,
        editPromoActive,
        parseInt(editPromoCredits),
        editPromoMaxUses ? parseInt(editPromoMaxUses) : null,
        editPromoExpires || null,
        editPromoDescription || null
      );

      if (result.success) {
        setPromoMessage({ type: 'success', text: 'Promo code updated successfully' });
        setEditingPromoId(null);
        await loadPromoCodes();
      } else {
        setPromoMessage({ type: 'error', text: result.error || 'Failed to update code' });
      }
    } catch (err: any) {
      setPromoMessage({ type: 'error', text: err.message || 'An error occurred' });
    } finally {
      setUpdatingPromo(false);
    }
  };

  const getPromoStatus = (promo: PromoCode) => {
    if (!promo.active) return { label: 'Inactive', color: 'bg-slate-700 text-slate-400' };
    if (promo.expires_at && new Date(promo.expires_at) < new Date()) {
      return { label: 'Expired', color: 'bg-red-900/30 text-red-400 border border-red-800' };
    }
    if (promo.max_uses && promo.current_uses >= promo.max_uses) {
      return { label: 'Exhausted', color: 'bg-yellow-900/30 text-yellow-400 border border-yellow-800' };
    }
    return { label: 'Active', color: 'bg-green-900/30 text-green-400 border border-green-800' };
  };

  const pendingOrders = orders.filter((o) => o.status === 'pending').length;
  const adminTabs: { id: AdminTab; label: string; icon: React.ElementType; badge?: number }[] = [];
  if (adminRole !== 'editor') {
    adminTabs.push(
      { id: 'activity', label: 'Activity', icon: Activity },
      { id: 'users', label: 'Users', icon: Users },
      { id: 'signups', label: 'Signups', icon: UserPlus },
      { id: 'ips', label: 'IPs', icon: Wifi },
      { id: 'domains', label: 'Domains', icon: Globe },
      { id: 'promos', label: 'Promos', icon: Gift },
      { id: 'sales', label: 'Sales', icon: BarChart3 },
      { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
    );
  }
  if (isSuperAdmin) {
    adminTabs.push({ id: 'editors', label: 'Editors', icon: Edit3 });
  }
  adminTabs.push({
    id: 'orders',
    label: 'Floor plans',
    icon: ClipboardList,
    badge: pendingOrders > 0 ? pendingOrders : undefined,
  });
  if (adminRole !== 'editor') {
    adminTabs.push(
      { id: 'generations', label: 'Generations', icon: Image },
      { id: 'imagemotion', label: 'ImageMotion', icon: Film },
      { id: 'tours', label: '360 Tours', icon: GlobeIcon },
      { id: 'shareDemos', label: 'Share demos', icon: Share2 },
      { id: 'aiTransparency', label: 'AI transparency', icon: Sparkles },
      { id: 'audit', label: 'Audit', icon: Shield },
    );
  }

  const adminPageCopy: Record<AdminTab, { title: string; description: string }> = {
    orders: { title: 'Floor plan orders', description: 'Process and deliver floor plans' },
    editors: { title: 'Editors', description: 'Assign editors and set who receives new orders' },
    activity: { title: 'Activity', description: 'Real-time event log' },
    users: { title: 'Users', description: 'PhotoClear activity. Credits, status and free HDR edits are in Hub.' },
    signups: { title: 'Signups', description: 'New user signups by day, auth method, app, and acquisition source' },
    ips: { title: 'IP addresses', description: 'Monitor and control IP addresses' },
    domains: { title: 'Domains', description: 'Monitor and control email domains' },
    promos: { title: 'Promo codes', description: 'Create and manage promotional codes' },
    audit: { title: 'Audit', description: 'View admin activity history' },
    generations: { title: 'Generations', description: 'Compare original and processed images' },
    sales: { title: 'Sales and costs', description: 'Profitability metrics and cost analysis' },
    subscriptions: { title: 'Subscriptions', description: 'ReHub credit plans and the payment that recorded each one' },
    imagemotion: { title: 'ImageMotion', description: 'All ImageMotion video projects across users' },
    tours: { title: '360 Tours', description: 'All 360 virtual tours across users' },
    shareDemos: { title: 'Share demos', description: 'Create and manage public before/after demo pages' },
    aiTransparency: { title: 'AI transparency', description: 'Article 50 transparency and provenance monitoring' },
  };

  const refreshAdmin = () => {
    if (activeTab === 'orders') loadOrders();
    else if (activeTab === 'editors') loadEditorsData();
    else if (activeTab === 'activity') loadActivityFeed();
    else if (activeTab === 'users') loadUsersWithStatus();
    else if (activeTab === 'ips') loadIPAnalytics();
    else if (activeTab === 'domains') loadDomainAnalytics();
    else if (activeTab === 'generations') loadGenerations();
    else if (activeTab === 'sales') {
      loadSalesMetrics();
      loadSalesTransactions();
    } else if (activeTab === 'imagemotion') loadImProjects();
    else if (activeTab === 'tours') loadAdminTours();
  };

  const canRefresh = activeTab === 'orders' || activeTab === 'editors' || activeTab === 'activity' || activeTab === 'users' || activeTab === 'ips' || activeTab === 'domains' || activeTab === 'generations' || activeTab === 'sales' || activeTab === 'imagemotion' || activeTab === 'tours' || activeTab === 'shareDemos';

  return (
    <AdminShell<AdminTab>
      scope="PhotoClear"
      role={adminRole}
      onBack={onClose}
      tabs={adminTabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      variant="overlay"
      title={adminPageCopy[activeTab].title}
      description={adminPageCopy[activeTab].description}
      actions={canRefresh ? (
        <button
          type="button"
          onClick={refreshAdmin}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          title="Refresh"
          aria-label="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      ) : undefined}
    >

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="flex items-center gap-4">
                <select
                  value={orderStatusFilter}
                  onChange={(e) => { setOrderStatusFilter(e.target.value as OrderStatus | ''); setTimeout(loadOrders, 100); }}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-teal-500"
                >
                  <option value="">All Orders</option>
                  <option value="pending_payment">Awaiting Payment</option>
                  <option value="pending">Pending</option>
                  <option value="quoted">Quoted</option>
                  <option value="paid">Paid</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button onClick={loadOrders} className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-700">
                  <RefreshCw size={16} className={loadingOrders ? 'animate-spin' : ''} /> Refresh
                </button>
                <span className="text-sm text-slate-400 ml-auto">{orders.length} orders</span>
              </div>
              {loadingOrders ? (
                <div className="flex justify-center py-12"><Loader2 size={32} className="animate-spin text-teal-400" /></div>
              ) : orders.length === 0 ? (
                <div className="bg-slate-900 rounded-xl border border-slate-800 p-8 text-center">
                  <ClipboardList size={48} className="mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">No orders found</p>
                </div>
              ) : (
                <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Order</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Property</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Size</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Turnaround</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Due by</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-slate-800/50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-bold text-orange-400">{order.job_number || '—'}</div>
                            <div className="text-xs text-slate-500">{new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-white max-w-[200px] truncate">{order.property_address}</div>
                            <div className="text-xs text-slate-500">{order.contact_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-white">{order.estimated_sq_ft?.toLocaleString() || '?'} sq ft</div>
                            <div className="text-xs text-slate-500">{order.floors} floor{order.floors !== 1 ? 's' : ''}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${order.turnaround === 'express' ? 'bg-amber-900/30 text-amber-400' : 'bg-slate-700 text-slate-300'}`}>
                              {order.turnaround === 'express' ? '24hr Express' : '48hr Standard'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className={`text-xs ${order.due_at && new Date(order.due_at) < new Date() && order.status !== 'completed' ? 'text-amber-400' : 'text-slate-300'}`}>
                              {order.due_at ? new Date(order.due_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {(() => { const s = badgeStyle(ORDER_STATUS_COLORS, order.status); return (
                            <span className={`text-xs font-medium px-2 py-1 rounded border ${s.bg} ${s.text}`}>
                              {s.label}
                            </span>
                            ); })()}
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => setSelectedOrder(order)} className="text-orange-400 hover:text-teal-300 text-sm font-medium">View Details</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                  <div className="bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
                    <div className="flex items-center justify-between p-6 border-b border-slate-700">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-bold text-white">Order Details</h3>
                          {selectedOrder.job_number && <span className="px-2 py-1 bg-teal-600/20 text-teal-300 border border-teal-500/30 text-xs font-bold rounded">{selectedOrder.job_number}</span>}
                        </div>
                        <p className="text-sm text-slate-400">{new Date(selectedOrder.created_at).toLocaleString('en-GB')}</p>
                      </div>
                      <button onClick={() => setSelectedOrder(null)} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700"><X size={20} /></button>
                    </div>
                    <div className="p-6 space-y-6">
                      <div className="flex items-center gap-4">
                        {(() => { const s = badgeStyle(ORDER_STATUS_COLORS, selectedOrder.status); return (
                        <span className={`text-sm font-medium px-3 py-1.5 rounded border ${s.bg} ${s.text}`}>
                          {s.label}
                        </span>
                        ); })()}
                        {selectedOrder.status === 'pending' && (
                          <button onClick={() => updateOrderStatus(selectedOrder.id, 'in_progress')} disabled={updatingOrder}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                            <Play size={16} /> Start Working
                          </button>
                        )}
                        {isSuperAdmin && (
                          <button onClick={() => deleteOrder(selectedOrder.id)} disabled={deletingOrderId === selectedOrder.id}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                            <Trash2 size={16} /> {deletingOrderId === selectedOrder.id ? 'Deleting...' : 'Delete Order'}
                          </button>
                        )}
                      </div>
                      <div className="bg-slate-900 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div><div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Ordered</div><div className="text-white font-medium">{new Date(selectedOrder.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}</div></div>
                        <div><div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Due by</div><div className={`font-medium ${selectedOrder.due_at && new Date(selectedOrder.due_at) < new Date() && selectedOrder.status !== 'completed' ? 'text-amber-400' : 'text-white'}`}>{selectedOrder.due_at ? new Date(selectedOrder.due_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</div></div>
                        <div><div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Started</div><div className="text-white font-medium">{selectedOrder.started_at ? new Date(selectedOrder.started_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</div></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-900 rounded-xl p-4">
                          <div className="text-slate-400 text-sm mb-2 flex items-center gap-2"><MapPin size={14} /> Property</div>
                          <div className="text-white font-medium">{selectedOrder.property_address}</div>
                          <div className="text-slate-400 text-sm mt-1">{selectedOrder.estimated_sq_ft?.toLocaleString() || '?'} sq ft • {selectedOrder.floors} floor{selectedOrder.floors !== 1 ? 's' : ''} • {selectedOrder.property_type}</div>
                        </div>
                        <div className="bg-slate-900 rounded-xl p-4">
                          <div className="text-slate-400 text-sm mb-2 flex items-center gap-2"><User size={14} /> Customer</div>
                          <div className="text-white font-medium">{selectedOrder.contact_email}</div>
                          {selectedOrder.contact_phone && <div className="text-slate-400 text-sm mt-1">{selectedOrder.contact_phone}</div>}
                        </div>
                      </div>
                      {selectedOrder.notes && (
                        <div className="bg-slate-900 rounded-xl p-4"><div className="text-slate-400 text-sm mb-2">Customer Notes</div><div className="text-white">{selectedOrder.notes}</div></div>
                      )}
                      <div className="bg-slate-900 rounded-xl p-4">
                        <div className="text-slate-400 text-sm mb-3">Customer Sketch</div>
                        {selectedOrder.sketch_url ? (
                          <div className="flex items-center gap-4">
                            {sketchDownloadUrl && !sketchDownloadUrl.startsWith('blob:') && !sketchLoadError ? (
                              <img src={sketchDownloadUrl} alt="Sketch" className="w-32 h-32 object-contain bg-white rounded-lg" onError={() => setSketchLoadError(true)} />
                            ) : (
                              <div className="w-32 h-32 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center text-xs text-slate-400">Sketch preview unavailable</div>
                            )}
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={async () => {
                                  const url = sketchDownloadUrl || getFirstSketchUrl(selectedOrder.sketch_url);
                                  if (!url || url.startsWith('blob:')) return;
                                  try {
                                    const res = await fetch(url);
                                    const blob = await res.blob();
                                    const ext = url.includes('.png') ? 'png' : url.includes('.webp') ? 'webp' : 'jpg';
                                    const name = `${selectedOrder.job_number || 'Sketch'}_${selectedOrder.property_address.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}.${ext}`;
                                    const a = document.createElement('a');
                                    a.href = window.URL.createObjectURL(blob);
                                    a.download = name;
                                    a.click();
                                    window.URL.revokeObjectURL(a.href);
                                  } catch { window.open(url, '_blank'); }
                                }}
                                disabled={!sketchDownloadUrl && !getFirstSketchUrl(selectedOrder.sketch_url)}
                                className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                              >
                                <Download size={16} /> Download Sketch
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-slate-400">No sketch uploaded.</div>
                        )}
                      </div>
                      <div className="bg-slate-900 rounded-xl p-4">
                        <div className="text-slate-400 text-sm mb-3">Completed Floor Plan</div>
                        {selectedOrder.output_url ? (
                          <div className="flex items-center gap-4">
                            <img src={selectedOrder.output_url} alt="Output" className="w-32 h-32 object-contain bg-white rounded-lg" />
                            <div className="flex flex-col gap-2">
                              <a href={selectedOrder.output_url} download target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium"><Download size={16} /> Download</a>
                              <button onClick={() => sendDeliveryEmail(selectedOrder)} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium"><Send size={16} /> Send to Customer</button>
                            </div>
                          </div>
                        ) : (
                          <label className="flex items-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium cursor-pointer">
                            <Upload size={16} />
                            {uploadingOutput ? 'Uploading...' : 'Upload Completed Floor Plan'}
                            <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadOutputFile(selectedOrder.id, f); }} disabled={uploadingOutput} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EDITORS TAB */}
          {activeTab === 'editors' && (
            <div className="max-w-4xl mx-auto p-6 space-y-8">
              {loadingEditors ? (
                <div className="flex justify-center py-12"><Loader2 size={32} className="animate-spin text-teal-400" /></div>
              ) : (
                <>
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Editors</h3>
                    <div className="mb-6 pb-6 border-b border-slate-800">
                      <p className="text-sm text-slate-400 mb-3">Create a new user and add them as an editor. No front-end signup needed.</p>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Email</label>
                          <input type="email" placeholder="editor@example.com" value={newEditorEmail} onChange={(e) => setNewEditorEmail(e.target.value.trim())} className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-64 focus:outline-none focus:border-teal-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Password (optional)</label>
                          <input type="password" placeholder="Min 8 characters" value={newEditorPassword} onChange={(e) => setNewEditorPassword(e.target.value)} className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-52 focus:outline-none focus:border-teal-500" />
                        </div>
                        <button onClick={async () => {
                          if (!newEditorEmail) return;
                          if (newEditorPassword && newEditorPassword.length < 8) { alert('Password must be at least 8 characters'); return; }
                          setCreatingEditor(true);
                          const res = await createEditorUser(newEditorEmail, newEditorPassword || undefined);
                          setCreatingEditor(false);
                          if (res.success) { setNewEditorEmail(''); setNewEditorPassword(''); loadEditorsData(); alert(res.password ? `Editor created. Share this password with them:\n\n${res.password}` : 'Editor created.'); }
                          else alert(res.error);
                        }} disabled={creatingEditor || !newEditorEmail} className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                          {creatingEditor ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create user & add as editor
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 mb-4">Or add existing user by User ID (UUID from Auth).</p>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <input type="text" placeholder="User ID (UUID)" value={newEditorUserId} onChange={(e) => setNewEditorUserId(e.target.value.trim())} className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-72 focus:outline-none focus:border-teal-500" />
                      <button onClick={async () => {
                        if (!newEditorUserId) return;
                        setAddingEditor(true);
                        const res = await addEditor(newEditorUserId);
                        setAddingEditor(false);
                        if (res.success) { setNewEditorUserId(''); loadEditorsData(); } else alert(res.error);
                      }} disabled={addingEditor || !newEditorUserId} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                        {addingEditor ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add existing user as editor
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {editors.length === 0 ? <li className="text-slate-500 text-sm">No editors yet.</li> : editors.map((row) => (
                        <li key={row.user_id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                          <span className="text-white font-mono text-sm">{row.user_id}</span>
                          <span className="text-slate-400 text-sm truncate max-w-[200px] ml-2">{row.email ?? '—'}</span>
                          <button onClick={async () => { if (!window.confirm('Remove this editor?')) return; const res = await removeEditor(row.user_id); if (res.success) loadEditorsData(); else alert(res.error); }} className="text-red-400 hover:text-red-300 text-sm font-medium">Remove</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Default editors (assignment order)</h3>
                    <p className="text-sm text-slate-400 mb-4">New orders are assigned to the first editor in this list.</p>
                    {defaultEditors.length === 0 ? <p className="text-slate-500 text-sm">No default editors.</p> : (
                      <ol className="list-decimal list-inside space-y-2 mb-4">
                        {defaultEditors.map((row) => (
                          <li key={row.user_id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                            <span className="text-white font-mono text-sm">{row.user_id}</span>
                            <span className="text-slate-400 text-sm truncate max-w-[200px] ml-2">{row.email ?? '—'}</span>
                            <button onClick={async () => { const next = defaultEditors.filter((r) => r.user_id !== row.user_id); const res = await setDefaultEditors(next.map((r) => r.user_id)); if (res.success) loadEditorsData(); else alert(res.error); }} className="text-red-400 hover:text-red-300 text-sm font-medium">Remove from default</button>
                          </li>
                        ))}
                      </ol>
                    )}
                    <div className="pt-4 border-t border-slate-800">
                      <label className="text-slate-400 text-sm block mb-2">Add editor to default list</label>
                      <select className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-full max-w-md focus:outline-none focus:border-teal-500" value="" onChange={async (e) => { const uid = e.target.value; if (!uid) return; const res = await setDefaultEditors([...defaultEditors.map((r) => r.user_id), uid]); if (res.success) loadEditorsData(); else alert(res.error); e.target.value = ''; }}>
                        <option value="">Select editor…</option>
                        {editors.filter((ed) => !defaultEditors.some((d) => d.user_id === ed.user_id)).map((row) => (
                          <option key={row.user_id} value={row.user_id}>{row.email || row.user_id}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ACTIVITY FEED TAB */}
          {activeTab === 'activity' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Filters */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                  <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter size={16} className="text-slate-400" />
                    <select
                      value={activityFilter}
                      onChange={(e) => {
                        setActivityFilter(e.target.value);
                        setTimeout(loadActivityFeed, 100);
                      }}
                      className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    >
                      <option value="">All Events</option>
                      <option value="magic_link_requested">Magic Link</option>
                      <option value="login">Login</option>
                      <option value="generation_started">Generation Started</option>
                      <option value="generation_completed">Generation Complete</option>
                      <option value="generation_failed">Generation Failed</option>
                      <option value="credits_granted">Credits Granted</option>
                      <option value="payment_completed">Payment</option>
                      <option value="promo_redeemed">Promo Redeemed</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-slate-400" />
                    <span className="text-xs text-slate-500">App:</span>
                    <div className="flex gap-1">
                      {['', 'photoclear', 'staging', 'sketchplan'].map((app) => (
                        <button
                          key={app || 'all'}
                          onClick={() => setActivityAppFilter(app)}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                            activityAppFilter === app
                              ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30'
                              : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                          }`}
                        >
                          {app || 'All'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="text-sm text-slate-500">
                    {activityTotal} total events
                  </span>
                </div>
              </div>

              {/* Event List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                {loadingActivity ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : activityEvents.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {(activityAppFilter
                      ? activityEvents.filter((e) => (e.metadata as Record<string, string> | null)?.source_app === activityAppFilter)
                      : activityEvents
                    ).map((event) => {
                      const typeInfo = EVENT_TYPE_LABELS[event.event_type] || { label: event.event_type, color: 'bg-slate-700 text-slate-400' };
                      const sourceApp = (event.metadata as Record<string, string> | null)?.source_app;
                      return (
                        <div
                          key={event.id}
                          className="p-4 hover:bg-slate-800/50 cursor-pointer transition-colors"
                          onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                              {sourceApp && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300 whitespace-nowrap">
                                  {sourceApp}
                                </span>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-white truncate">
                                  {event.user_email || 'Unknown user'}
                                </p>
                                {event.ip_address && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    IP: {event.ip_address}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xs text-slate-400">
                                {new Date(event.created_at).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                          {selectedEvent?.id === event.id && event.metadata && Object.keys(event.metadata).length > 0 && (
                            <div className="mt-3 p-3 bg-black rounded-lg">
                              <p className="text-xs text-slate-500 mb-2">Metadata:</p>
                              <pre className="text-xs text-slate-300 overflow-x-auto">
                                {JSON.stringify(event.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Activity size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No events yet</h4>
                    <p className="text-sm text-slate-500">Events will appear here as they occur</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* USERS & STATUS TAB */}
          {activeTab === 'users' && !selectedUserDetail && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Filters */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by email..."
                      className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-500"
                      onKeyDown={(e) => e.key === 'Enter' && searchUsersWithStatus()}
                    />
                  </div>
                  <select
                    value={userStatusFilter}
                    onChange={(e) => {
                      const nextFilter = e.target.value as UserRiskStatus | '';
                      setUserStatusFilter(nextFilter);
                      setUsersPage(0);
                      loadUsersWithStatus(0, nextFilter);
                    }}
                    className="bg-black border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="ok">OK</option>
                    <option value="watch">Watch</option>
                    <option value="restricted">Restricted</option>
                    <option value="banned">Banned</option>
                  </select>
                  <button
                    onClick={searchUsersWithStatus}
                    className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 font-medium text-sm transition-colors"
                  >
                    <Search size={16} />
                    Search
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-3">{usersTotal} users found</p>
              </div>

              {/* Users List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                {loadingUsers ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : usersWithStatus.length > 0 ? (
                  <>
                    <div className="divide-y divide-slate-800">
                      {usersWithStatus.map((u) => {
                        const statusInfo = badgeStyle(STATUS_COLORS, u.risk_status);
                        return (
                          <div key={u.id} className="p-4 hover:bg-slate-800/50 transition-colors">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setRouteUserId(u.id)}
                                      className="text-sm font-medium text-white truncate text-left hover:text-teal-300"
                                    >
                                      {u.email}
                                    </button>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusInfo.bg} ${statusInfo.text}`}>
                                      {statusInfo.label}
                                    </span>
                                    {u.marketing_consent && (
                                      <span
                                        className="px-2 py-0.5 rounded text-xs font-medium border bg-emerald-500/10 text-emerald-400 border-emerald-500/30 flex items-center gap-1"
                                        title={u.marketing_consent_at ? `Opted in ${new Date(u.marketing_consent_at).toLocaleDateString()}` : 'Opted into marketing'}
                                      >
                                        <Mail size={11} />
                                        Marketing
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                    <span>{u.credits} credits</span>
                                    <button
                                      type="button"
                                      onClick={() => void openHubAdminUser(supabase, u.id)}
                                      className="text-sky-400 hover:text-sky-300"
                                    >
                                      Hub account
                                    </button>
                                    <span>{u.event_count} events</span>
                                    {u.last_activity && (
                                      <span>Last: {new Date(u.last_activity).toLocaleDateString()}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setRouteUserId(u.id)}
                                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                                  title="Open user"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void copyUserUrl(u.id)}
                                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                                  title={copiedUserId === u.id ? 'Copied' : 'Copy user URL'}
                                >
                                  {copiedUserId === u.id ? <Check size={16} className="text-green-400" /> : <Link2 size={16} />}
                                </button>
                                <button
                                  onClick={() => {
                                    setStatusChangeUser(u);
                                    setNewStatus(u.risk_status);
                                    setStatusReason(u.status_reason || '');
                                  }}
                                  className="p-2 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded-lg transition-colors"
                                  title="Change Status"
                                >
                                  <Edit3 size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {usersTotal > USERS_PAGE_SIZE && (
                      <div className="p-4 border-t border-slate-800 flex items-center justify-between">
                        <span className="text-sm text-slate-500">
                          Showing {usersPage * USERS_PAGE_SIZE + 1} - {Math.min((usersPage + 1) * USERS_PAGE_SIZE, usersTotal)} of {usersTotal}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const prev = Math.max(0, usersPage - 1);
                              setUsersPage(prev);
                              loadUsersWithStatus(prev);
                            }}
                            disabled={usersPage === 0 || loadingUsers}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => {
                              const next = usersPage + 1;
                              setUsersPage(next);
                              loadUsersWithStatus(next);
                            }}
                            disabled={(usersPage + 1) * USERS_PAGE_SIZE >= usersTotal || loadingUsers}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Users size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No users found</h4>
                    <p className="text-sm text-slate-500">Try adjusting your search</p>
                  </div>
                )}
              </div>

              {/* Status Change Modal */}
              {statusChangeUser && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-4">Change User Status</h3>
                    <p className="text-sm text-slate-400 mb-4">{statusChangeUser.email}</p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                        <select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value as UserRiskStatus)}
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        >
                          <option value="ok">OK - Normal access</option>
                          <option value="watch">Watch - Monitor activity</option>
                          <option value="restricted">Restricted - Limited access</option>
                          <option value="banned">Banned - No access</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                        <input
                          type="text"
                          value={statusReason}
                          onChange={(e) => setStatusReason(e.target.value)}
                          placeholder="Reason for status change"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setStatusChangeUser(null)}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateUserStatus}
                        disabled={updatingStatus}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {updatingStatus ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* USER DETAIL VIEW */}
          {activeTab === 'users' && selectedUserDetail && (
            <div className="max-w-5xl mx-auto space-y-6">
              <button
                onClick={() => {
                  requestedUserIdRef.current = null;
                  setSelectedUserDetail(null);
                  setRouteUserId(null);
                  setUserDetailTab('timeline');
                  setUserGenerations([]);
                  setUserCreditLedger([]);
                  setUserPayments([]);
                  setUserCost(null);
                }}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={18} />
                Back to Users
              </button>

              {/* User Info Card */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-white">{selectedUserDetail.user.email}</h3>
                    <div className="flex items-center gap-3 mt-2">
                      {(() => { const s = badgeStyle(STATUS_COLORS, selectedUserDetail.user.risk_status); return (
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${s.bg} ${s.text}`}>
                        {s.label}
                      </span>
                      ); })()}
                      <span className="text-sm text-orange-400 flex items-center gap-1">
                        <Coins size={14} />
                        {selectedUserDetail.user.credits} credits
                      </span>
                      <button
                        type="button"
                        onClick={() => void openHubAdminUser(supabase, selectedUserDetail.user.id)}
                        className="text-sm text-sky-400 hover:text-sky-300"
                      >
                        Free HDR edits in Hub
                      </button>
                    </div>
                    {selectedUserDetail.user.status_reason && (
                      <p className="text-sm text-slate-500 mt-2">Reason: {selectedUserDetail.user.status_reason}</p>
                    )}
                    {userCost && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-400">
                        {(userCost.plan || userCost.plan_id) && (
                          <span>
                            Plan: <span className="text-white capitalize">{userCost.plan || userCost.plan_id}</span>
                            {userCost.plan_id && userCost.plan ? <span className="text-slate-500"> · {userCost.plan_id}</span> : null}
                          </span>
                        )}
                        <span>
                          Paid: <span className="text-white">{formatAdminMoney(userCost.total_paid_cents, userCost.currency)}</span>
                        </span>
                        <span>
                          Granted: <span className="text-white">{userCost.credits_granted}</span>
                        </span>
                        <span>
                          Used: <span className="text-white">{userCost.credits_used}</span>
                        </span>
                        {userCost.credits_refunded > 0 && (
                          <span>
                            Refunded: <span className="text-white">{userCost.credits_refunded}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-500 block">
                      Joined: {new Date(selectedUserDetail.user.created_at).toLocaleDateString()}
                    </span>
                    {selectedUserDetail.user.signup_app && (
                      <span className="text-xs text-slate-400 block mt-0.5">
                        Signed up via: <span className="capitalize text-slate-300">{selectedUserDetail.user.signup_app}</span>
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => void copyUserUrl(selectedUserDetail.user.id, userDetailTab)}
                      className="mt-2 text-xs text-slate-400 hover:text-white flex items-center gap-1"
                      title="Copy link to this user"
                    >
                      {copiedUserId === selectedUserDetail.user.id ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                      {copiedUserId === selectedUserDetail.user.id ? 'Copied' : adminUserPath(selectedUserDetail.user.id, userDetailTab)}
                    </button>
                    {userCost?.stripe_customer_id && (
                      <a
                        href={`https://dashboard.stripe.com/customers/${userCost.stripe_customer_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-xs text-slate-500 hover:text-teal-300 flex items-center justify-end gap-1"
                      >
                        Stripe customer <ExternalLink size={11} />
                      </a>
                    )}
                    <button
                      onClick={() => setShowAdjustCredits(true)}
                      className="mt-2 text-xs bg-teal-600 hover:bg-teal-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <CreditCard size={12} />
                      Adjust Credits
                    </button>
                  </div>
                </div>
              </div>

              {/* User Location Map (from event IPs) */}
              <UserLocationMap events={selectedUserDetail.events} />

              {/* App usage summary (from all resources) */}
              {userResources.length > 0 && (() => {
                const byApp: Record<string, { count: number; credits: number }> = {};
                userResources.forEach((r) => {
                  const app = r.app || 'unknown';
                  if (!byApp[app]) byApp[app] = { count: 0, credits: 0 };
                  byApp[app].count += 1;
                  byApp[app].credits += r.credits_used || 0;
                });
                const entries = Object.entries(byApp);
                if (entries.length === 0) return null;
                return (
                  <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5">
                    <span className="text-xs text-slate-500">Resources by app: </span>
                    {entries.map(([app, { count, credits }]) => (
                      <span key={app} className="text-xs text-slate-300 mr-3">
                        <span className="text-slate-400">{USER_RESOURCE_APP_LABELS[app] || app}</span>{' '}
                        {count} ({credits > 0 ? `${credits} cr` : '—'})
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Tab Navigation */}
              <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-xl p-2">
                <button
                  onClick={() => setUserDetailTab('timeline')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userDetailTab === 'timeline' ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent'
                  }`}
                >
                  <Activity size={16} className="inline mr-2" />
                  Timeline
                </button>
                <button
                  onClick={() => setUserDetailTab('generations')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userDetailTab === 'generations' ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent'
                  }`}
                >
                  <Image size={16} className="inline mr-2" />
                  Gallery ({userResourcesTotal || userResources.length})
                </button>
                <button
                  onClick={() => setUserDetailTab('credits')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userDetailTab === 'credits' ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent'
                  }`}
                >
                  <Coins size={16} className="inline mr-2" />
                  Credit Ledger
                </button>
                <button
                  onClick={() => setUserDetailTab('payments')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    userDetailTab === 'payments' ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent'
                  }`}
                >
                  <CreditCard size={16} className="inline mr-2" />
                  Payments
                </button>
              </div>

              {/* Timeline Tab */}
              {userDetailTab === 'timeline' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800">
                    <h4 className="font-semibold text-white">Activity Timeline</h4>
                    <p className="text-sm text-slate-500">{selectedUserDetail.events.length} events</p>
                  </div>
                  {loadingUserDetail ? (
                    <div className="flex justify-center p-12">
                      <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                    </div>
                  ) : selectedUserDetail.events.length > 0 ? (
                    <div className="divide-y divide-slate-800 max-h-[500px] overflow-y-auto">
                      {selectedUserDetail.events.map((event) => {
                        const typeInfo = EVENT_TYPE_LABELS[event.event_type] || { label: event.event_type, color: 'bg-slate-700 text-slate-400' };
                        return (
                          <div key={event.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(event.created_at).toLocaleString()}
                              </span>
                            </div>
                            {event.ip_address && (
                              <p className="text-xs text-slate-500 mt-2">IP: {event.ip_address}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-slate-500">No activity recorded</p>
                    </div>
                  )}
                </div>
              )}

              {/* Resources Gallery Tab */}
              {userDetailTab === 'generations' && (() => {
                const appFilters = Array.from(new Set(userResources.map((r) => r.app))).sort();
                const filteredResources = userResourceAppFilter
                  ? userResources.filter((r) => r.app === userResourceAppFilter)
                  : userResources;
                const generationResources = filteredResources.filter((r) => r.kind === 'generation');
                const otherResources = filteredResources.filter((r) => r.kind !== 'generation');
                const genById = new Map(userGenerations.map((g) => [g.id, g]));

                return (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="font-semibold text-white">User Resources</h4>
                      <p className="text-sm text-slate-500">
                        {filteredResources.length} shown
                        {userResourcesTotal > userResources.length ? ` of ${userResourcesTotal}` : ''}
                        {' · '}images, videos, tours, brochures &amp; more
                      </p>
                    </div>
                    {appFilters.length > 1 && (
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => setUserResourceAppFilter('')}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            !userResourceAppFilter ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'bg-slate-800 text-slate-400 hover:text-white'
                          }`}
                        >
                          All
                        </button>
                        {appFilters.map((app) => (
                          <button
                            key={app}
                            onClick={() => setUserResourceAppFilter(app)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                              userResourceAppFilter === app ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30' : 'bg-slate-800 text-slate-400 hover:text-white'
                            }`}
                          >
                            {USER_RESOURCE_APP_LABELS[app] || app}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {loadingUserGenerations ? (
                    <div className="flex justify-center p-12">
                      <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                    </div>
                  ) : filteredResources.length > 0 ? (
                    <div className="p-4 space-y-6">
                      {generationResources.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                            Generated images ({generationResources.length})
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {generationResources.map((res) => {
                              const gen = genById.get(res.id);
                              const statusInfo = GENERATION_STATUS_COLORS[(gen?.status || res.status) as GenerationStatus] || GENERATION_STATUS_COLORS.queued;
                              const inputPath = gen?.input_asset_path || (res.meta as any)?.input_asset_path || res.preview_path;
                              const outputPath = gen?.output_image_path || (res.meta as any)?.output_image_path || res.output_path;
                              const outputUrl = outputPath ? getImageUrl(outputPath) : null;
                              const inputUrl = inputPath ? getImageUrl(inputPath) : null;
                              const lightboxGen: Generation = gen || {
                                id: res.id,
                                created_at: res.created_at,
                                user_id: selectedUserDetail?.user.id ?? null,
                                user_email: selectedUserDetail?.user.email ?? null,
                                status: res.status as GenerationStatus,
                                credits_used: res.credits_used || 0,
                                input_asset_path: inputPath,
                                output_image_path: outputPath,
                                ip_address: null,
                                metadata: (res.meta as any)?.metadata || { source_app: res.app },
                                completed_at: null,
                                error_message: (res.meta as any)?.error_message || null,
                              };
                              return (
                                <div key={res.id} className="bg-black rounded-xl overflow-hidden border border-slate-800">
                                  <div className="grid grid-cols-2 gap-px bg-slate-800">
                                    <AdminGenImage
                                      path={inputPath}
                                      alt="Original"
                                      status={res.status}
                                      onOpen={() => openGenerationLightbox(lightboxGen, 'original', userGenerations.length ? userGenerations : [lightboxGen])}
                                    >
                                      <span className="absolute top-2 left-2 bg-black/70 text-slate-300 text-[10px] font-medium px-2 py-0.5 rounded pointer-events-none">
                                        Original
                                      </span>
                                    </AdminGenImage>
                                    <AdminGenImage
                                      path={outputPath}
                                      alt="Processed"
                                      status={res.status}
                                      onOpen={() => openGenerationLightbox(lightboxGen, 'processed', userGenerations.length ? userGenerations : [lightboxGen])}
                                    >
                                      <span className="absolute top-2 left-2 bg-teal-700/90 text-white text-[10px] font-medium px-2 py-0.5 rounded pointer-events-none">
                                        Processed
                                      </span>
                                      <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium pointer-events-none ${statusInfo.bg} ${statusInfo.text}`}>
                                        {statusInfo.label}
                                      </span>
                                    </AdminGenImage>
                                  </div>
                                  <div className="p-3">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="text-xs text-slate-400">
                                        {new Date(res.created_at).toLocaleDateString()}
                                      </p>
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                                          {USER_RESOURCE_APP_LABELS[res.app] || res.app}
                                        </span>
                                        {(res.credits_used || 0) > 0 && (
                                          <span className="text-xs text-orange-400">{res.credits_used} cr</span>
                                        )}
                                      </div>
                                    </div>
                                    {(gen?.error_message || (res.meta as any)?.error_message) && (
                                      <p className="text-xs text-red-400 mt-1 truncate" title={gen?.error_message || (res.meta as any)?.error_message}>
                                        {gen?.error_message || (res.meta as any)?.error_message}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-1 mt-2">
                                      {inputUrl && (
                                        <button
                                          onClick={() => handleDownload(inputUrl, `${res.id}-original`)}
                                          disabled={downloadingId === `${res.id}-original`}
                                          className="flex-1 text-xs text-slate-400 hover:text-white flex items-center justify-center gap-1 bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                          title="Download Original"
                                        >
                                          {downloadingId === `${res.id}-original` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                          Original
                                        </button>
                                      )}
                                      {outputUrl && (
                                        <button
                                          onClick={() => handleDownload(outputUrl, res.id)}
                                          disabled={downloadingId === res.id}
                                          className="flex-1 text-xs text-teal-300 hover:text-white flex items-center justify-center gap-1 bg-teal-900/30 hover:bg-teal-900/50 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                          title="Download Processed"
                                        >
                                          {downloadingId === res.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                          Result
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {otherResources.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                            Other resources ({otherResources.length})
                          </h5>
                          <div className="space-y-2">
                            {otherResources.map((res) => {
                              const KindIcon =
                                res.kind === 'imagemotion_project' || res.kind === 'videomotion_project' ? Film
                                : res.kind === 'tour' ? GlobeIcon
                                : res.kind === 'brochure' ? FileText
                                : res.kind === 'showcase' ? Monitor
                                : res.kind === 'pro_order' ? ClipboardList
                                : res.kind === 'recording' ? Play
                                : Image;
                              return (
                                <div
                                  key={`${res.kind}-${res.id}`}
                                  className="flex items-center gap-3 bg-black/40 border border-slate-800 rounded-xl px-3 py-2.5"
                                >
                                  <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 shrink-0">
                                    <KindIcon size={16} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-sm text-white truncate">{res.title}</p>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                                        {USER_RESOURCE_APP_LABELS[res.app] || res.app}
                                      </span>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-500">
                                        {USER_RESOURCE_KIND_LABELS[res.kind] || res.kind}
                                      </span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {new Date(res.created_at).toLocaleString()}
                                      {' · '}
                                      <span className="capitalize">{res.status}</span>
                                      {(res.credits_used || 0) > 0 ? ` · ${res.credits_used} cr` : ''}
                                    </p>
                                  </div>
                                  {res.kind === 'imagemotion_project' && (res.preview_path || res.output_path) && (
                                    <button
                                      type="button"
                                      onClick={() => openImageMotionVideo(res.id, res.title, res.preview_path, res.output_path)}
                                      disabled={loadingVideoId === res.id}
                                      className="text-xs text-teal-300 hover:text-white flex items-center gap-1 shrink-0 bg-teal-900/30 hover:bg-teal-900/50 px-2 py-1 rounded disabled:opacity-50"
                                    >
                                      {loadingVideoId === res.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                                      Watch
                                    </button>
                                  )}
                                  {res.link_url && (
                                    <a
                                      href={res.link_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-orange-400 hover:text-teal-300 flex items-center gap-1 shrink-0"
                                    >
                                      Open <ExternalLink size={12} />
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Image size={48} className="mx-auto text-slate-700 mb-4" />
                      <p className="text-sm text-slate-500">No resources yet</p>
                    </div>
                  )}
                </div>
                );
              })()}

              {/* Credit Ledger Tab */}
              {userDetailTab === 'credits' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-white">Credit Ledger</h4>
                      <p className="text-sm text-slate-500">
                        Current balance: <span className="text-orange-400 font-semibold">{userCreditBalance}</span>
                        {userCreditLedgerTotal > 0 && (
                          <span>
                            {' · '}
                            {userCreditLedger.length === userCreditLedgerTotal
                              ? `${userCreditLedgerTotal} entries`
                              : `${userCreditLedger.length} of ${userCreditLedgerTotal} entries`}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {loadingCreditLedger ? (
                    <div className="flex justify-center p-12">
                      <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                    </div>
                  ) : userCreditLedger.length > 0 ? (
                    <div className="divide-y divide-slate-800 max-h-[500px] overflow-y-auto">
                      {userCreditLedger.map((entry) => {
                        const sourceApp = (entry.metadata as Record<string, string> | null)?.source_app || (entry.metadata as Record<string, string> | null)?.app;
                        return (
                          <div key={entry.id} className="p-4 flex items-center justify-between">
                            <div>
                              <p className="text-sm text-white">{entry.reason}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {entry.ref_type && (
                                  <span className="text-xs bg-slate-700 px-2 py-0.5 rounded text-slate-400">
                                    {entry.ref_type}
                                  </span>
                                )}
                                {sourceApp && (
                                  <span className="text-xs bg-orange-900/40 text-orange-300 px-2 py-0.5 rounded capitalize">
                                    {sourceApp}
                                  </span>
                                )}
                                <span className="text-xs text-slate-500">
                                  {new Date(entry.created_at).toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <span className={`text-lg font-bold ${
                              entry.delta > 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {entry.delta > 0 ? '+' : ''}{entry.delta}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Coins size={48} className="mx-auto text-slate-700 mb-4" />
                      <p className="text-sm text-slate-500">No credit history</p>
                    </div>
                  )}
                </div>
              )}

              {/* Payments Tab */}
              {userDetailTab === 'payments' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800">
                    <h4 className="font-semibold text-white">Payments & credit additions</h4>
                    <p className="text-sm text-slate-500">Stripe charges, subscription grants, purchases, and refunds</p>
                  </div>
                  {loadingUserPayments ? (
                    <div className="flex justify-center p-12">
                      <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                    </div>
                  ) : userPayments.length > 0 ? (
                    <div className="divide-y divide-slate-800 max-h-[500px] overflow-y-auto">
                      {userPayments.map((payment) => (
                        <div key={payment.id} className="p-4 flex items-center justify-between">
                          <div>
                            <p className="text-sm text-white">{payment.description}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                payment.type === 'purchase' ? 'bg-green-900/40 text-green-400' :
                                payment.type === 'bonus' ? 'bg-slate-700 text-slate-300' :
                                'bg-amber-900/40 text-amber-400'
                              }`}>
                                {payment.type}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(payment.created_at).toLocaleString()}
                              </span>
                              {payment.stripe_session_id && (
                                <span className="text-xs text-slate-400" title={payment.stripe_session_id}>
                                  Stripe session
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className={`text-lg font-bold ${
                              (payment.amount_cents ?? payment.amount) > 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {payment.amount_cents != null
                                ? formatAdminMoney(payment.amount_cents, payment.currency)
                                : `${payment.amount > 0 ? '+' : ''}${payment.amount} cr`}
                            </span>
                            {payment.amount_cents != null && (
                              <p className="text-xs text-slate-500">
                                {payment.amount > 0 ? '+' : ''}{payment.amount} cr
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CreditCard size={48} className="mx-auto text-slate-700 mb-4" />
                      <p className="text-sm text-slate-500">No payments or credit additions</p>
                    </div>
                  )}
                </div>
              )}

              {/* Adjust Credits Modal */}
              {showAdjustCredits && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-4">Adjust Credits</h3>
                    <p className="text-sm text-slate-400 mb-4">{selectedUserDetail.user.email}</p>
                    <p className="text-sm text-slate-400 mb-4">
                      Current balance: <span className="text-orange-400 font-semibold">{selectedUserDetail.user.credits}</span>
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                        <input
                          type="number"
                          value={adjustCreditsAmount}
                          onChange={(e) => setAdjustCreditsAmount(e.target.value)}
                          placeholder="e.g., 10 or -5"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                        <p className="text-xs text-slate-500 mt-1">Positive to add, negative to deduct</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason *</label>
                        <input
                          type="text"
                          value={adjustCreditsReason}
                          onChange={(e) => setAdjustCreditsReason(e.target.value)}
                          placeholder="Reason for adjustment"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => {
                          setShowAdjustCredits(false);
                          setAdjustCreditsAmount('');
                          setAdjustCreditsReason('');
                        }}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAdjustCredits}
                        disabled={adjustingCredits || !adjustCreditsAmount || !adjustCreditsReason.trim()}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {adjustingCredits ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Adjust
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* IP ANALYTICS TAB */}
          {activeTab === 'ips' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {ipError && (
                <div className="bg-red-900/50 border border-red-700 rounded-xl p-4 text-red-200 text-sm">
                  <strong>Error loading IP analytics:</strong> {ipError}
                  <p className="mt-1 text-red-300/70">Run the migration <code className="bg-red-900 px-1 rounded">20260319100000_fix_ip_logging_and_analytics.sql</code> in the Supabase SQL Editor.</p>
                </div>
              )}
              {/* World Map */}
              <IPWorldMap ipAnalytics={ipAnalytics} accentColor="#f97316" />

              {/* IP List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <h4 className="font-semibold text-white">Top IPs by Email Count</h4>
                  <select
                    value={ipDays}
                    onChange={(e) => {
                      const d = parseInt(e.target.value);
                      setIpDays(d);
                      loadIPAnalytics(d);
                    }}
                    className="bg-black border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-teal-500"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={180}>Last 6 months</option>
                    <option value={0}>All time</option>
                  </select>
                </div>
                {loadingIPs ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : ipAnalytics.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {ipAnalytics.map((ip) => {
                      const controlInfo = badgeStyle(CONTROL_COLORS, ip.control_status);
                      const emailCount = ip.email_count || 0;
                      const isSuspicious = emailCount > 3 || ip.user_count > 3;
                      const isHighRisk = emailCount > 5;
                      return (
                        <div key={ip.ip_address} className="p-4 hover:bg-slate-800/50 transition-colors">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <code className="text-sm text-white font-mono">{ip.ip_address}</code>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${controlInfo.bg} ${controlInfo.text}`}>
                                  {controlInfo.label}
                                </span>
                                {isHighRisk ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-900/40 text-red-300 border border-red-700 flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    High Risk — {emailCount} emails
                                  </span>
                                ) : isSuspicious && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-800 flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    Suspicious — {emailCount} emails
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                <span>{ip.event_count} events</span>
                                <span>{ip.user_count} logged-in users</span>
                                <span className={emailCount > ip.user_count ? 'text-yellow-500' : ''}>{emailCount} emails</span>
                                <span>First: {new Date(ip.first_activity).toLocaleDateString()}</span>
                                <span>Last: {new Date(ip.last_activity).toLocaleDateString()}</span>
                              </div>
                              {(ip.emails?.length ?? 0) > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {ip.emails!.map((email) => (
                                    <span
                                      key={email}
                                      className="px-2 py-0.5 rounded-md bg-black/40 border border-slate-700 text-[11px] text-slate-200 font-mono break-all"
                                    >
                                      {email}
                                    </span>
                                  ))}
                                  {emailCount > ip.emails!.length && (
                                    <span className="px-2 py-0.5 text-[11px] text-slate-500">
                                      +{emailCount - ip.emails!.length} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setSelectedIP(ip);
                                setIpControlStatus(ip.control_status);
                                setIpThrottle(ip.throttle_multiplier.toString());
                                setIpControlReason(ip.control_reason || '');
                                setIpControlError(null);
                              }}
                              className="p-2 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded-lg transition-colors"
                              title="Manage IP"
                            >
                              <Edit3 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Wifi size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No IP data yet</h4>
                    <p className="text-sm text-slate-500">IP analytics will appear as events are logged</p>
                  </div>
                )}
              </div>

              {/* IP Control Modal */}
              {selectedIP && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-2">IP Control</h3>
                    <code className="text-sm text-orange-400 font-mono">{selectedIP.ip_address}</code>
                    {(selectedIP.emails?.length ?? 0) > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-400 mb-1.5">Emails on this IP</p>
                        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                          {selectedIP.emails!.map((email) => (
                            <span
                              key={email}
                              className="px-2 py-0.5 rounded-md bg-black/40 border border-slate-700 text-[11px] text-slate-200 font-mono break-all"
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                        <select
                          value={ipControlStatus}
                          onChange={(e) => setIpControlStatus(e.target.value as ControlStatus)}
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        >
                          <option value="ok">OK - Normal</option>
                          <option value="throttled">Throttled - Rate limited</option>
                          <option value="blocked">Blocked - No access</option>
                        </select>
                      </div>
                      {ipControlStatus === 'throttled' && (
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Throttle Multiplier</label>
                          <input
                            type="number"
                            min="0.1"
                            max="10"
                            step="0.1"
                            value={ipThrottle}
                            onChange={(e) => setIpThrottle(e.target.value)}
                            className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                          />
                          <p className="text-xs text-slate-500 mt-1">Higher = stricter limit (2.0 = half the normal rate)</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                        <input
                          type="text"
                          value={ipControlReason}
                          onChange={(e) => setIpControlReason(e.target.value)}
                          placeholder="Reason for control"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    {ipControlError && (
                      <p className="mt-4 text-sm text-red-400">{ipControlError}</p>
                    )}

                    <div className="flex gap-3 mt-6">
                      <button
                        type="button"
                        onClick={() => setSelectedIP(null)}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleUpdateIPControl}
                        disabled={updatingIP}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {updatingIP ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* DOMAIN ANALYTICS TAB */}
          {activeTab === 'domains' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Domain List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-800">
                  <h4 className="font-semibold text-white">Top Domains (Last 30 Days)</h4>
                </div>
                {loadingDomains ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : domainAnalytics.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {domainAnalytics.map((domain) => {
                      const controlInfo = badgeStyle(CONTROL_COLORS, domain.control_status);
                      return (
                        <div key={domain.domain} className="p-4 hover:bg-slate-800/50 transition-colors">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-white font-medium">@{domain.domain}</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${controlInfo.bg} ${controlInfo.text}`}>
                                  {controlInfo.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                <span>{domain.user_count} users</span>
                                <span>{domain.event_count} events</span>
                                <span>Last: {new Date(domain.last_activity).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setSelectedDomain(domain);
                                setDomainControlStatus(domain.control_status);
                                setDomainThrottle(domain.throttle_multiplier.toString());
                                setDomainControlReason(domain.control_reason || '');
                              }}
                              className="p-2 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded-lg transition-colors"
                              title="Manage Domain"
                            >
                              <Edit3 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Globe size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No domain data yet</h4>
                    <p className="text-sm text-slate-500">Domain analytics will appear as events are logged</p>
                  </div>
                )}
              </div>

              {/* Domain Control Modal */}
              {selectedDomain && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
                    <h3 className="text-lg font-semibold text-white mb-2">Domain Control</h3>
                    <span className="text-sm text-orange-400">@{selectedDomain.domain}</span>
                    
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                        <select
                          value={domainControlStatus}
                          onChange={(e) => setDomainControlStatus(e.target.value as DomainControlStatus)}
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        >
                          <option value="ok">OK - Normal</option>
                          <option value="throttled">Throttled - Rate limited</option>
                          <option value="require_payment">Require Payment - No free credits</option>
                          <option value="blocked">Blocked - No signups</option>
                        </select>
                      </div>
                      {domainControlStatus === 'throttled' && (
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">Throttle Multiplier</label>
                          <input
                            type="number"
                            min="0.1"
                            max="10"
                            step="0.1"
                            value={domainThrottle}
                            onChange={(e) => setDomainThrottle(e.target.value)}
                            className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                          />
                          <p className="text-xs text-slate-500 mt-1">Higher = stricter limit</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                        <input
                          type="text"
                          value={domainControlReason}
                          onChange={(e) => setDomainControlReason(e.target.value)}
                          placeholder="Reason for control"
                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setSelectedDomain(null)}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateDomainControl}
                        disabled={updatingDomain}
                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        {updatingDomain ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PROMO CODES TAB */}
          {activeTab === 'promos' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Codes</p>
                      <p className="text-3xl font-bold text-white mt-1">{promoCodes.length}</p>
                    </div>
                    <Gift size={32} className="text-orange-400" />
                  </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Active Codes</p>
                      <p className="text-3xl font-bold text-green-400 mt-1">
                        {promoCodes.filter(p => p.active && (!p.expires_at || new Date(p.expires_at) > new Date())).length}
                      </p>
                    </div>
                    <CheckCircle size={32} className="text-green-400" />
                  </div>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Redemptions</p>
                      <p className="text-3xl font-bold text-orange-400 mt-1">{redemptions.length}</p>
                    </div>
                    <TrendingUp size={32} className="text-orange-400" />
                  </div>
                </div>
              </div>

              {/* Create Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCreatePromo(!showCreatePromo)}
                  className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-semibold transition-colors"
                >
                  {showCreatePromo ? <XCircle size={18} /> : <Plus size={18} />}
                  {showCreatePromo ? 'Cancel' : 'Create Promo Code'}
                </button>
              </div>

              {/* Create Form */}
              {showCreatePromo && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-6">Create New Promo Code</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Code *</label>
                      <input
                        type="text"
                        value={newPromoCode}
                        onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                        placeholder="e.g., WELCOME50"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500 uppercase tracking-wide"
                      />
                      <p className="text-xs text-slate-500 mt-1">3-20 alphanumeric characters</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Credits *</label>
                      <input
                        type="number"
                        min="1"
                        value={newPromoCredits}
                        onChange={(e) => setNewPromoCredits(e.target.value)}
                        placeholder="e.g., 10"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Max Uses</label>
                      <input
                        type="number"
                        min="1"
                        value={newPromoMaxUses}
                        onChange={(e) => setNewPromoMaxUses(e.target.value)}
                        placeholder="Leave empty for unlimited"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Expires At</label>
                      <input
                        type="datetime-local"
                        value={newPromoExpires}
                        onChange={(e) => setNewPromoExpires(e.target.value)}
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                      <input
                        type="text"
                        value={newPromoDescription}
                        onChange={(e) => setNewPromoDescription(e.target.value)}
                        placeholder="Optional internal note"
                        className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleCreatePromo}
                    disabled={creatingPromo || !newPromoCode || !newPromoCredits}
                    className="w-full mt-6 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                  >
                    {creatingPromo ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                    Create Promo Code
                  </button>

                  {promoMessage && (
                    <div className={`mt-4 p-4 rounded-lg flex items-center gap-2 text-sm ${promoMessage.type === 'success' ? 'bg-green-900/30 border border-green-800 text-green-400' : 'bg-red-900/30 border border-red-800 text-red-400'}`}>
                      {promoMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                      {promoMessage.text}
                    </div>
                  )}
                </div>
              )}

              {/* Promo Codes List */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-800">
                  <h3 className="text-lg font-semibold text-white">All Promo Codes</h3>
                </div>

                {loadingPromos ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : promoCodes.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-black/50 border-b border-slate-800 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="p-4">Code</th>
                          <th className="p-4">Credits</th>
                          <th className="p-4">Usage</th>
                          <th className="p-4">Expires</th>
                          <th className="p-4">Status</th>
                          <th className="p-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-sm text-slate-300">
                        {promoCodes.map((promo) => {
                          const status = getPromoStatus(promo);
                          const isEditing = editingPromoId === promo.id;
                          
                          if (isEditing) {
                            return (
                              <tr key={promo.id} className="bg-slate-800/50">
                                <td colSpan={6} className="p-6">
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                      <h4 className="text-white font-semibold">Edit Promo Code: <code className="text-orange-400">{promo.code}</code></h4>
                                      <button
                                        onClick={handleCancelEditPromo}
                                        className="text-slate-400 hover:text-white"
                                      >
                                        <XCircle size={20} />
                                      </button>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Credits *</label>
                                        <input
                                          type="number"
                                          value={editPromoCredits}
                                          onChange={(e) => setEditPromoCredits(e.target.value)}
                                          min="1"
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Max Uses</label>
                                        <input
                                          type="number"
                                          value={editPromoMaxUses}
                                          onChange={(e) => setEditPromoMaxUses(e.target.value)}
                                          placeholder="Unlimited"
                                          min="0"
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited</p>
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Expires At</label>
                                        <input
                                          type="date"
                                          value={editPromoExpires}
                                          onChange={(e) => setEditPromoExpires(e.target.value)}
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">Leave empty for no expiration</p>
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                                        <select
                                          value={editPromoActive ? 'active' : 'inactive'}
                                          onChange={(e) => setEditPromoActive(e.target.value === 'active')}
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        >
                                          <option value="active">Active</option>
                                          <option value="inactive">Inactive</option>
                                        </select>
                                      </div>
                                      
                                      <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                                        <textarea
                                          value={editPromoDescription}
                                          onChange={(e) => setEditPromoDescription(e.target.value)}
                                          placeholder="Optional description"
                                          rows={2}
                                          className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                                        />
                                      </div>
                                    </div>
                                    
                                    <div className="flex gap-3 pt-2">
                                      <button
                                        onClick={() => handleUpdatePromo(promo.id)}
                                        disabled={updatingPromo || !editPromoCredits}
                                        className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-700 text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                                      >
                                        {updatingPromo ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                                        Save Changes
                                      </button>
                                      <button
                                        onClick={handleCancelEditPromo}
                                        disabled={updatingPromo}
                                        className="px-6 bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg font-semibold transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          
                          return (
                            <tr key={promo.id} className="hover:bg-slate-800/50">
                              <td className="p-4">
                                <code className="text-orange-400 font-mono font-semibold">{promo.code}</code>
                                {promo.description && (
                                  <p className="text-xs text-slate-500 mt-1">{promo.description}</p>
                                )}
                              </td>
                              <td className="p-4 font-bold text-white">{promo.credits}</td>
                              <td className="p-4">
                                <span className="text-slate-400">{promo.current_uses}</span>
                                {promo.max_uses && (
                                  <span className="text-slate-500"> / {promo.max_uses}</span>
                                )}
                                {!promo.max_uses && <span className="text-slate-500"> / ∞</span>}
                              </td>
                              <td className="p-4 text-slate-400 text-xs">
                                {promo.expires_at 
                                  ? new Date(promo.expires_at).toLocaleDateString()
                                  : 'Never'
                                }
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                                  {status.label}
                                </span>
                              </td>
                              <td className="p-4">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleStartEditPromo(promo)}
                                    className="px-3 py-1 rounded-lg text-xs font-medium bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 transition-colors"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleTogglePromo(promo.id, promo.active)}
                                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                                      promo.active 
                                        ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' 
                                        : 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                                    }`}
                                  >
                                    {promo.active ? 'Deactivate' : 'Activate'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Gift size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No promo codes yet</h4>
                    <p className="text-sm text-slate-500">Create your first promo code to get started</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AUDIT LOG TAB */}
          {activeTab === 'audit' && (
            <div className="max-w-6xl mx-auto">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-800">
                  <h3 className="text-lg font-semibold text-white">Recent Admin Actions</h3>
                  <p className="text-sm text-slate-500 mt-1">Last 50 administrative actions</p>
                </div>

                {loadingAudit ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : auditLog.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-black/50 border-b border-slate-800 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="p-4">Date</th>
                          <th className="p-4">Action</th>
                          <th className="p-4">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-sm text-slate-300">
                        {auditLog.map((action) => (
                          <tr key={action.id} className="hover:bg-slate-800/50">
                            <td className="p-4 text-slate-400 whitespace-nowrap">
                              {new Date(action.created_at).toLocaleDateString()}{' '}
                              <span className="text-xs opacity-70">
                                {new Date(action.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                action.action_type === 'grant_credits' 
                                  ? 'bg-green-900/30 text-green-400' 
                                  : 'bg-blue-900/30 text-blue-400'
                              }`}>
                                {action.action_type.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-4">
                              {action.action_type === 'grant_credits' && action.details && (
                                <div className="text-xs">
                                  <p className="text-white">+{action.details.amount} credits to {action.details.target_email}</p>
                                  <p className="text-slate-500 mt-1">{action.details.reason}</p>
                                </div>
                              )}
                              {action.action_type === 'create_promo' && action.details && (
                                <div className="text-xs">
                                  <p className="text-white">Created code: <code className="text-orange-400">{action.details.code}</code></p>
                                  <p className="text-slate-500 mt-1">{action.details.credits} credits</p>
                                </div>
                              )}
                              {action.action_type === 'update_promo' && action.details && (
                                <div className="text-xs">
                                  <p className="text-white">Updated <code className="text-orange-400">{action.details.code}</code></p>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Activity size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No activity yet</h4>
                    <p className="text-sm text-slate-500">Admin actions will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GENERATIONS TAB */}
          {activeTab === 'generations' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Stuck Generations Alert */}
              {stuckCount > 0 && (
                <div className="bg-yellow-900/20 border border-yellow-800 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle size={24} className="text-yellow-400" />
                      <div>
                        <h4 className="text-yellow-400 font-medium">{stuckCount} Stuck Generation{stuckCount !== 1 ? 's' : ''}</h4>
                        <p className="text-sm text-yellow-400/70">Generations stuck in "started" status for more than 15 minutes</p>
                      </div>
                    </div>
                    <button
                      onClick={handleCleanupStuck}
                      disabled={cleaningStuck}
                      className="bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-700 text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
                    >
                      {cleaningStuck ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                      Cleanup & Refund
                    </button>
                  </div>
                  {cleanupResult && (
                    <div className="mt-3 p-3 bg-green-900/30 border border-green-800 rounded-lg">
                      <p className="text-sm text-green-400">
                        ✓ Cleaned {cleanupResult.cleaned} generation{cleanupResult.cleaned !== 1 ? 's' : ''}, refunded {cleanupResult.refunded} credit{cleanupResult.refunded !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Filters */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      value={generationEmailFilter}
                      onChange={(e) => setGenerationEmailFilter(e.target.value)}
                      placeholder="Filter by user email..."
                      className="w-full bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-500"
                      onKeyDown={(e) => e.key === 'Enter' && loadGenerations()}
                    />
                  </div>
                  <select
                    value={generationStatusFilter}
                    onChange={(e) => {
                      setGenerationStatusFilter(e.target.value as GenerationStatus | '');
                      setGenerationsPage(0);
                      setTimeout(loadGenerations, 100);
                    }}
                    className="bg-black border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="queued">Queued</option>
                    <option value="started">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                  <button
                    onClick={() => {
                      setGenerationsPage(0);
                      loadGenerations();
                    }}
                    className="bg-teal-600 hover:bg-teal-500 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 font-medium text-sm transition-colors"
                  >
                    <Search size={16} />
                    Search
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-400">From:</label>
                    <input
                      type="date"
                      value={generationDateFrom}
                      onChange={(e) => setGenerationDateFrom(e.target.value)}
                      className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-400">To:</label>
                    <input
                      type="date"
                      value={generationDateTo}
                      onChange={(e) => setGenerationDateTo(e.target.value)}
                      className="bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  {(generationDateFrom || generationDateTo) && (
                    <button
                      onClick={() => {
                        setGenerationDateFrom('');
                        setGenerationDateTo('');
                        setGenerationsPage(0);
                        setTimeout(loadGenerations, 100);
                      }}
                      className="text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      Clear dates
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                  <p className="text-sm text-slate-500">{generationsTotal} total generations</p>
                  <div className="flex items-center gap-2" title="Images per row">
                    <span className="text-xs text-slate-500">Size</span>
                    <input
                      type="range"
                      min={1}
                      max={8}
                      step={1}
                      value={generationGridCols}
                      onChange={(e) => updateGenerationGridCols(Number(e.target.value))}
                      className="w-32 accent-teal-500"
                    />
                    <span className="text-xs text-slate-400 w-10 whitespace-nowrap">{generationGridCols} / row</span>
                  </div>
                </div>
              </div>

              {/* Generations Grid */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                {loadingGenerations ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : generations.length > 0 ? (
                  <>
                    <div
                      className="grid gap-4 p-4"
                      style={{ gridTemplateColumns: `repeat(${generationGridCols}, minmax(0, 1fr))` }}
                    >
                      {generations.map((gen) => {
                        const statusInfo = GENERATION_STATUS_COLORS[gen.status as GenerationStatus] || GENERATION_STATUS_COLORS.queued;
                        const outputUrl = gen.output_image_path ? getImageUrl(gen.output_image_path) : null;
                        const inputUrl = gen.input_asset_path ? getImageUrl(gen.input_asset_path) : null;
                        return (
                          <div key={gen.id} className="bg-black rounded-xl overflow-hidden border border-slate-800 hover:border-slate-700 transition-colors">
                            {/* Side by side images */}
                            <div className="grid grid-cols-2 gap-px bg-slate-800">
                              <AdminGenImage
                                path={gen.input_asset_path}
                                alt="Original"
                                status={gen.status}
                                thumb={generationThumb}
                                onOpen={() => openGenerationLightbox(gen, 'original', generations)}
                              >
                                <span className="absolute top-2 left-2 bg-black/70 text-slate-300 text-[10px] font-medium px-2 py-0.5 rounded pointer-events-none">
                                  Original
                                </span>
                              </AdminGenImage>
                              <AdminGenImage
                                path={gen.output_image_path}
                                alt="Processed"
                                status={gen.status}
                                thumb={generationThumb}
                                onOpen={() => openGenerationLightbox(gen, 'processed', generations)}
                              >
                                <span className="absolute top-2 left-2 bg-teal-700/90 text-white text-[10px] font-medium px-2 py-0.5 rounded pointer-events-none">
                                  Processed
                                </span>
                                <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium pointer-events-none ${statusInfo.bg} ${statusInfo.text}`}>
                                  {statusInfo.label}
                                </span>
                              </AdminGenImage>
                            </div>
                            <div className="p-3">
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-white truncate" title={gen.user_email || 'Unknown'}>
                                  {gen.user_email || 'Unknown user'}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {new Date(gen.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                {gen.user_id && (
                                  <button
                                    onClick={() => {
                                      setUserDetailTab('timeline');
                                      setActiveTab('users');
                                      setRouteUserId(gen.user_id!);
                                    }}
                                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                  >
                                    <User size={12} />
                                    User
                                  </button>
                                )}
                                {(gen.metadata as Record<string, string> | null)?.source_app && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 capitalize">
                                    {(gen.metadata as Record<string, string>).source_app}
                                  </span>
                                )}
                                {gen.credits_used > 0 && (
                                  <span className="text-xs text-orange-400">{gen.credits_used} cr</span>
                                )}
                                <div className="flex items-center gap-1 ml-auto">
                                  {inputUrl && (
                                    <button
                                      onClick={() => handleDownload(inputUrl, `${gen.id}-original`)}
                                      disabled={downloadingId === `${gen.id}-original`}
                                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1 bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                      title="Download Original"
                                    >
                                      {downloadingId === `${gen.id}-original` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                      Orig
                                    </button>
                                  )}
                                  {outputUrl && (
                                    <button
                                      onClick={() => handleDownload(outputUrl, gen.id)}
                                      disabled={downloadingId === gen.id}
                                      className="text-xs text-teal-300 hover:text-white flex items-center gap-1 bg-teal-900/30 hover:bg-teal-900/50 px-2 py-1 rounded transition-colors disabled:opacity-50"
                                      title="Download Processed"
                                    >
                                      {downloadingId === gen.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                      Result
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Pagination */}
                    {generationsTotal > 12 && (
                      <div className="p-4 border-t border-slate-800 flex items-center justify-between">
                        <span className="text-sm text-slate-500">
                          Showing {generationsPage * 12 + 1} - {Math.min((generationsPage + 1) * 12, generationsTotal)} of {generationsTotal}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setGenerationsPage(p => Math.max(0, p - 1));
                              setTimeout(loadGenerations, 100);
                            }}
                            disabled={generationsPage === 0}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => {
                              setGenerationsPage(p => p + 1);
                              setTimeout(loadGenerations, 100);
                            }}
                            disabled={(generationsPage + 1) * 12 >= generationsTotal}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Image size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No generations found</h4>
                    <p className="text-sm text-slate-500">Generations will appear here as users process images</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'subscriptions' && (
            <div className="max-w-6xl mx-auto">
              <SubscriptionsTab
                onOpenUser={(userId) => {
                  setUserDetailTab('payments');
                  setActiveTab('users');
                  setRouteUserId(userId);
                }}
              />
            </div>
          )}

          {/* SALES & COSTS TAB */}
          {activeTab === 'sales' && (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Period Filter */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-400">Period:</span>
                  <div className="flex gap-2">
                    {(['today', '7d', '30d', 'all'] as SalesPeriod[]).map((period) => (
                        <button
                        key={period}
                        onClick={() => applySalesPeriod(period)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          salesPeriod === period
                            ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        {period === 'today' ? 'From today' : period === '7d' ? 'Last 7 Days' : period === '30d' ? 'Last 30 Days' : 'All Time'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Record manual sale - for payments taken outside Stripe (e.g. bank transfer) */}
              <RecordManualSaleForm onRecorded={() => {
                loadSalesMetrics();
                applySalesSearch();
              }} />

              {loadingSales ? (
                <div className="flex justify-center p-12">
                  <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                </div>
              ) : salesMetrics ? (
                <>
                  {/* Key Metrics Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Gross Revenue */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">Gross Revenue</p>
                          <p className="text-2xl md:text-3xl font-bold text-green-400 mt-1">
                            {formatRecordedRevenue(salesMetrics)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {recordedRevenueCaption(salesMetrics)}
                          </p>
                        </div>
                        <DollarSign size={32} className="text-green-400" />
                      </div>
                    </div>

                    {/* Total Cost */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">API Costs</p>
                          <p className="text-2xl md:text-3xl font-bold text-red-400 mt-1">
                            ${salesMetrics.total_cost_usd.toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {salesMetrics.completed_generations} generations · USD
                          </p>
                        </div>
                        <Target size={32} className="text-red-400" />
                      </div>
                    </div>

                    {/* Gross Margin */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400">Stripe amounts</p>
                          <p className="text-2xl md:text-3xl font-bold text-white mt-1">
                            {salesMetrics.sales_with_amount ?? 0}
                            <span className="text-lg text-slate-500"> / {(salesMetrics.sales_with_amount ?? 0) + (salesMetrics.sales_without_amount ?? 0)}</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Sales with the amount Stripe charged
                          </p>
                        </div>
                        <Percent size={32} className="text-slate-500" />
                      </div>
                    </div>
                  </div>

                  {/* Secondary Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Credits Used */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" title="Sum of all credits deducted (consume_credit) in this period. Photoclear = 1 cr/image, Staging = 5 cr/image.">
                      <p className="text-xs text-slate-500">Credits Used</p>
                      <p className="text-2xl font-bold text-white mt-1">{salesMetrics.credits_used}</p>
                      <p className="text-[10px] text-slate-600 mt-0.5">Total consumed in period</p>
                    </div>

                    {/* Credits Refunded */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Credits Refunded</p>
                      <p className="text-2xl font-bold text-yellow-400 mt-1">{salesMetrics.credits_refunded}</p>
                    </div>

                    {/* Failed Rate */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Failed Rate</p>
                      <p className={`text-2xl font-bold mt-1 ${
                        salesMetrics.failed_rate_pct <= 5 ? 'text-green-400' : 
                        salesMetrics.failed_rate_pct <= 15 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {salesMetrics.failed_rate_pct}%
                      </p>
                      <p className="text-xs text-slate-500">
                        {salesMetrics.failed_generations} / {salesMetrics.total_generations}
                      </p>
                    </div>

                    {/* Avg Cost/Gen */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <p className="text-xs text-slate-500">Avg Cost / Gen</p>
                      <p className="text-lg font-bold text-white mt-1">
                        {formatProviderCostUsd(salesMetrics.avg_cost_per_generation, { generations: salesMetrics.completed_generations })}
                      </p>
                    </div>
                  </div>

                  {/* Profitability Analysis */}
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Profitability Analysis</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Cash collected</span>
                        <span className="text-white font-medium">{formatRecordedRevenue(salesMetrics)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Avg API cost per generation</span>
                        <span className="text-white font-medium">{formatProviderCostUsd(salesMetrics.avg_cost_per_generation, { generations: salesMetrics.completed_generations })}</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-slate-400">Credits granted</span>
                        <span className="text-white font-medium">{salesMetrics.credits_granted ?? salesMetrics.credits_sold}</span>
                      </div>
                    </div>

                    {(salesMetrics.sales_with_amount ?? 0) > 0 && (
                      <div className="mt-6 p-4 rounded-xl bg-black">
                        <p className="text-sm text-slate-400 mb-2">Status</p>
                        <p className="text-slate-200 font-medium">Cash is shown in the currency Stripe charged. API cost stays in US dollars, so these are not combined into one margin.</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-16 px-6">
                  <BarChart3 size={48} className="mx-auto text-slate-700 mb-4" />
                  <h4 className="text-slate-300 font-medium mb-1">No sales data</h4>
                  <p className="text-sm text-slate-500">Metrics will appear once purchases are made</p>
                </div>
              )}

              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Transactions</h3>
                    <p className="text-sm text-slate-500">
                      Purchases and subscriptions in this period{salesTransactionsTotal > 0 ? ` · ${salesTransactionsTotal}` : ''}
                    </p>
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      applySalesSearch();
                    }}
                    className="flex items-center gap-2"
                  >
                    <input
                      type="search"
                      value={salesSearchInput}
                      onChange={(e) => setSalesSearchInput(e.target.value)}
                      placeholder="Search email or description"
                      className="w-56 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    />
                    <button
                      type="submit"
                      className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
                    >
                      Search
                    </button>
                    {salesSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setSalesSearchInput('');
                          applySalesSearch('');
                        }}
                        className="px-3 py-2 rounded-lg text-slate-400 hover:text-white text-sm"
                      >
                        Clear
                      </button>
                    )}
                  </form>
                </div>

                {loadingSalesTransactions ? (
                  <div className="flex justify-center p-12">
                    <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                  </div>
                ) : salesTransactions.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-900/80 text-slate-400 border-b border-slate-800">
                          <tr>
                            <th className="px-4 py-3 font-medium">Date</th>
                            <th className="px-4 py-3 font-medium">Customer</th>
                            <th className="px-4 py-3 font-medium">Description</th>
                            <th className="px-4 py-3 font-medium text-right">Credits</th>
                            <th className="px-4 py-3 font-medium text-right">Revenue</th>
                            <th className="px-4 py-3 font-medium">Source</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                          {salesTransactions.map((tx) => {
                            const isSubscription = tx.type === 'subscription' || tx.type === 'subscription_renewal';
                            const isManual = /manual/i.test(tx.description || '');
                            const source = isSubscription ? 'Subscription' : tx.stripe_session_id ? 'Stripe' : isManual ? 'Manual' : 'Purchase';
                            const creditsGranted = tx.credits_granted ?? tx.amount;
                            return (
                              <tr key={tx.id} className="hover:bg-slate-800/40">
                                <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                                  {new Date(tx.created_at).toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-slate-200 truncate max-w-[220px]">
                                  {tx.email || '—'}
                                </td>
                                <td className="px-4 py-3 text-slate-400 truncate max-w-[280px]" title={tx.description}>
                                  {tx.description}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-green-400 whitespace-nowrap">
                                  +{creditsGranted}
                                </td>
                                <td className="px-4 py-3 text-right text-slate-200 whitespace-nowrap">
                                  {formatChargedAmount(tx.paid_amount_cents, tx.paid_currency)}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      source === 'Subscription' ? 'bg-teal-900/40 text-teal-300' :
                                      source === 'Stripe' ? 'bg-indigo-900/40 text-indigo-300' :
                                      source === 'Manual' ? 'bg-amber-900/40 text-amber-300' :
                                      'bg-slate-800 text-slate-300'
                                    }`}>
                                      {source}
                                    </span>
                                    {tx.currency && (
                                      <span className="text-xs text-slate-500">{tx.currency}</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {salesTransactionsTotal > SALES_TX_PAGE_SIZE && (
                      <div className="p-4 border-t border-slate-800 flex items-center justify-between">
                        <span className="text-sm text-slate-500">
                          Showing {salesTransactionsPage * SALES_TX_PAGE_SIZE + 1} – {Math.min((salesTransactionsPage + 1) * SALES_TX_PAGE_SIZE, salesTransactionsTotal)} of {salesTransactionsTotal}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const prev = Math.max(0, salesTransactionsPage - 1);
                              setSalesTransactionsPage(prev);
                              loadSalesTransactions(salesPeriod, prev);
                            }}
                            disabled={salesTransactionsPage === 0 || loadingSalesTransactions}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => {
                              const next = salesTransactionsPage + 1;
                              setSalesTransactionsPage(next);
                              loadSalesTransactions(salesPeriod, next);
                            }}
                            disabled={(salesTransactionsPage + 1) * SALES_TX_PAGE_SIZE >= salesTransactionsTotal || loadingSalesTransactions}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 px-6">
                    <CreditCard size={40} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No sales in this period</h4>
                    <p className="text-sm text-slate-500">
                      {salesSearch
                        ? 'Try a different search, or clear the filter'
                        : salesPeriod === 'all'
                          ? 'Purchases and subscriptions will appear here once customers pay'
                          : 'Try Last 30 Days or All Time to see earlier sales'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== IMAGEMOTION TAB ===== */}
          {activeTab === 'imagemotion' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={imStatusFilter}
                  onChange={(e) => setImStatusFilter(e.target.value)}
                  className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2"
                >
                  <option value="">All statuses</option>
                  <option value="queued">Queued</option>
                  <option value="generating_clips">Generating Clips</option>
                  <option value="editing">Editing</option>
                  <option value="rendering">Rendering</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                </select>
                <span className="text-sm text-slate-500">
                  {(() => {
                    const filtered = imStatusFilter ? imProjects.filter(p => p.status === imStatusFilter) : imProjects;
                    return `${filtered.length} project${filtered.length !== 1 ? 's' : ''}`;
                  })()}
                </span>
              </div>

              {loadingImProjects ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : (() => {
                const filtered = imStatusFilter ? imProjects.filter(p => p.status === imStatusFilter) : imProjects;
                return filtered.length === 0 ? (
                  <div className="text-center py-16 px-6">
                    <Film size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No ImageMotion projects found</h4>
                    <p className="text-sm text-slate-500">Projects will appear here once users create them</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-800 overflow-hidden">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-900/80 text-slate-400 border-b border-slate-800">
                        <tr>
                          <th className="px-4 py-3 font-medium">Project</th>
                          <th className="px-4 py-3 font-medium">Owner</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                          <th className="px-4 py-3 font-medium">Quality</th>
                          <th className="px-4 py-3 font-medium text-center">Images</th>
                          <th className="px-4 py-3 font-medium text-center">Shared</th>
                          <th className="px-4 py-3 font-medium">Created</th>
                          <th className="px-4 py-3 font-medium w-20"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {filtered.map((p) => (
                          <tr key={p.id} className="hover:bg-slate-900/50">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                {p.is_promoted && (
                                  <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0" title="Active Space" />
                                )}
                                <span className="text-slate-200 font-medium truncate max-w-[200px]">{p.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-slate-400 truncate max-w-[180px]">
                              {p.owner_email || (p.user_id ? '—' : <span className="text-slate-600 italic">Anonymous preview</span>)}
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                p.status === 'completed' ? 'bg-green-900/40 text-green-400' :
                                p.status === 'failed' ? 'bg-red-900/40 text-red-400' :
                                p.status === 'rendering' || p.status === 'generating_clips' ? 'bg-blue-900/40 text-blue-400' :
                                p.status === 'expired' ? 'bg-orange-900/40 text-orange-400' :
                                'bg-slate-700/60 text-slate-400'
                              }`}>
                                {p.status}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`text-xs ${
                                p.quality === 'ultra_premium' ? 'text-amber-400' :
                                p.quality === 'premium' ? 'text-purple-400' :
                                'text-slate-400'
                              }`}>
                                {p.quality === 'ultra_premium' ? 'Ultra' : p.quality === 'premium' ? 'Premium' : 'Standard'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-slate-400 text-center">{p.image_count}</td>
                            <td className="px-4 py-2.5 text-center">
                              {p.share_token ? (
                                <CheckCircle size={14} className="text-green-400 mx-auto" />
                              ) : (
                                <span className="text-slate-600">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500 text-xs">
                              {new Date(p.created_at).toLocaleDateString(undefined, { dateStyle: 'short' })}
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5">
                                {(p.final_video_path || p.final_video_url) && (
                                  <button
                                    type="button"
                                    onClick={() => openImageMotionVideo(p.id, p.name, p.final_video_path, p.final_video_url)}
                                    disabled={loadingVideoId === p.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-700/60 text-slate-300 hover:bg-slate-600/60 text-xs font-medium disabled:opacity-50"
                                  >
                                    {loadingVideoId === p.id ? (
                                      <Loader2 size={12} className="animate-spin" />
                                    ) : (
                                      <Play size={12} />
                                    )}
                                    Watch
                                  </button>
                                )}
                                {p.share_token && (
                                  <a
                                    href={`https://imagemotion.rehub.studio/view/${p.share_token}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 text-xs font-medium"
                                  >
                                    <Eye size={12} />
                                    View
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ===== 360 TOURS TAB ===== */}
          {activeTab === 'tours' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={tourStatusFilter}
                  onChange={(e) => setTourStatusFilter(e.target.value)}
                  className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2"
                >
                  <option value="">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
                <span className="text-sm text-slate-500">
                  {(() => {
                    const filtered = tourStatusFilter ? adminTours.filter(t => t.status === tourStatusFilter) : adminTours;
                    return `${filtered.length} tour${filtered.length !== 1 ? 's' : ''}`;
                  })()}
                </span>
              </div>

              {loadingAdminTours ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : (() => {
                const filtered = tourStatusFilter ? adminTours.filter(t => t.status === tourStatusFilter) : adminTours;
                return filtered.length === 0 ? (
                  <div className="text-center py-16 px-6">
                    <GlobeIcon size={48} className="mx-auto text-slate-700 mb-4" />
                    <h4 className="text-slate-300 font-medium mb-1">No 360 tours found</h4>
                    <p className="text-sm text-slate-500">Tours will appear here once users create them</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-800 overflow-hidden">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-900/80 text-slate-400 border-b border-slate-800">
                        <tr>
                          <th className="px-4 py-3 font-medium">Tour</th>
                          <th className="px-4 py-3 font-medium">Owner</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                          <th className="px-4 py-3 font-medium">Hosting</th>
                          <th className="px-4 py-3 font-medium text-center">Scenes</th>
                          <th className="px-4 py-3 font-medium text-right">Views</th>
                          <th className="px-4 py-3 font-medium">Build</th>
                          <th className="px-4 py-3 font-medium">Created</th>
                          <th className="px-4 py-3 font-medium w-20"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {filtered.map((t) => (
                          <tr key={t.id} className="hover:bg-slate-900/50">
                            <td className="px-4 py-2.5 text-slate-200 font-medium truncate max-w-[200px]">{t.title}</td>
                            <td className="px-4 py-2.5 text-slate-400 truncate max-w-[180px]">{t.owner_email || '—'}</td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                t.status === 'published' ? 'bg-green-900/40 text-green-400' :
                                t.status === 'draft' ? 'bg-slate-600/40 text-slate-400' :
                                'bg-slate-700/40 text-slate-500'
                              }`}>
                                {t.status}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`text-xs ${
                                t.hosting_status === 'active' ? 'text-green-400' :
                                t.hosting_status === 'expired' ? 'text-red-400' :
                                'text-slate-500'
                              }`}>
                                {t.hosting_status}
                                {t.hosting_plan ? ` (${t.hosting_plan})` : ''}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-slate-400 text-center">{t.scene_count}</td>
                            <td className="px-4 py-2.5 text-slate-400 text-right tabular-nums">{t.view_count}</td>
                            <td className="px-4 py-2.5">
                              {t.build_mode && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  t.build_mode === 'STUDIO' ? 'bg-purple-900/40 text-purple-400' : 'text-slate-500'
                                }`}>
                                  {t.build_mode}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500 text-xs">
                              {new Date(t.created_at).toLocaleDateString(undefined, { dateStyle: 'short' })}
                            </td>
                            <td className="px-4 py-2.5">
                              <a
                                href={`https://360tours.rehub.studio/view/${encodeURIComponent(t.slug)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-teal-600/20 text-teal-300 hover:bg-teal-600/30 text-xs font-medium"
                              >
                                <Eye size={12} />
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ===== SIGNUPS TAB ===== */}
          {activeTab === 'signups' && (
            <AdminSignupsPanel />
          )}

          {/* ===== SHARE DEMOS TAB ===== */}
          {activeTab === 'shareDemos' && (
            <ShareDemoManager userId={user.id} />
          )}

          {/* ===== AI TRANSPARENCY TAB ===== */}
          {activeTab === 'aiTransparency' && (
            <div className="max-w-5xl mx-auto">
              <AdminAITransparencyPanel />
            </div>
          )}

      {videoPreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setVideoPreview(null)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-800">
              <h3 className="text-white font-semibold truncate">{videoPreview.title}</h3>
              <button
                type="button"
                onClick={() => setVideoPreview(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
                aria-label="Close video"
              >
                <X size={18} />
              </button>
            </div>
            <div className="bg-black">
              {videoPreview.items[videoPreview.active] ? (
                <video
                  key={videoPreview.items[videoPreview.active].url}
                  src={videoPreview.items[videoPreview.active].url}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[70vh] bg-black"
                />
              ) : (
                <div className="py-16 text-center text-sm text-slate-500">This video is not available to play.</div>
              )}
            </div>
            {videoPreview.items.length > 1 && (
              <div className="flex flex-wrap gap-1.5 p-3 border-t border-slate-800">
                {videoPreview.items.map((item, index) => (
                  <button
                    key={`${item.label}-${index}`}
                    type="button"
                    onClick={() => setVideoPreview((prev) => (prev ? { ...prev, active: index } : prev))}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                      videoPreview.active === index
                        ? 'bg-teal-600/20 text-teal-300 border border-teal-500/30'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {generationLightbox && (
        <AdminGenerationLightbox
          state={generationLightbox}
          list={generationLightboxList}
          downloadingId={downloadingId}
          onClose={() => setGenerationLightbox(null)}
          onNavigate={(gen, mode) => openGenerationLightbox(gen, mode, generationLightboxList)}
          onModeChange={(mode) =>
            setGenerationLightbox((prev) => (prev ? { ...prev, mode } : prev))
          }
          onDownload={handleDownload}
        />
      )}
    </AdminShell>
  );
}
